import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import FusionLogo from '../components/FusionLogo';
import SupplierFooter from '../components/SupplierFooter';
import 'react-datepicker/dist/react-datepicker.css';
import { supabase } from '../supabaseClient';
import { useNavigate, useParams } from 'react-router-dom';
import { sendEmail, supplierResponseEmail, supplierAcceptedEmail, supplierDeclinedEmail, extensionRequestedEmail } from '../lib/email';
import { diffBriefVersions, hasBriefChanges } from '../lib/briefDiff';
import BriefChangesPanel from '../components/brief/BriefChangesPanel';
import { scrollBehavior } from '../lib/motion';
import { markChangesSeen } from '../lib/changesSeen';
import MoneyInput from '../components/MoneyInput';
import { fmtDate, fmtDateTime, localTimezoneAbbr } from '../lib/dates';
import AwardMedal from '../components/AwardMedal';
import AutoSizeTextarea from '../components/AutoSizeTextarea';
import AttachmentViewerModal from '../components/AttachmentViewerModal';
import SupplierAccountMenu from '../components/SupplierAccountMenu';
import SupplierSlideout from '../components/SupplierSlideout';
import SupplierBriefFactsPanel from '../components/SupplierBriefFactsPanel';
import InfoIcon from '../components/InfoIcon';
import { portalStatusMeta } from '../components/portal/PortalStatusPill';
import { deadlineUrgency } from '../lib/deadlineUrgency';
import { deriveClosedOutcome, CLOSED_OUTCOME, CLOSED_OUTCOME_COPY } from '../lib/closedBriefOutcome';
import { downloadFile } from '../lib/downloadFile';
import { StreamersMark } from '../components/StreamersMark';
import BriefQuestions from '../components/brief/BriefQuestions';
import BriefPostDeadline from '../components/brief/BriefPostDeadline';
import Alert from '../components/Alert';
import { useNow } from '../hooks/useNow';
import { extractName } from '../lib/contactName';
import { formatGBP as gbp } from '../lib/money';
import '../lib/quill-theme.css';
import '../lib/datepicker-theme.css';

// True when the value looks like HTML (Quill output). Legacy plain-text
// briefs render with whitespace-pre-wrap so their line breaks survive.
const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024; // 10 MB per file
const ALLOWED_ATTACHMENT_EXTS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'];
const ALLOWED_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*';

const formatFileSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const formatGBP = (n) => gbp(n, { pence: 'auto', fallback: '£0' });

// BRIEF-28 §6b – clamp the VAT rate to a valid UK range (0–20%). Stops an
// out-of-range value (e.g. a stray 30%) from being accepted, displayed, or
// saved. The input defaults to 20%.
const clampVat = (v) => Math.max(0, Math.min(20, Number(v) || 0));

// BRIEF-25 §1 – the shared result panel: a tinted #F7F9F0 inset with the
// grand total emphasised in deep-green above a divider. One component so the
// quote card, the review modal, and the submitted-response summary all read
// money identically. Pass the ex-VAT subtotal + VAT rate; it derives the
// rest so the three surfaces can't drift.
const ResultPanel = ({ subtotal, vatRate, className = '' }) => {
  const net = Number(subtotal) || 0;
  const rate = clampVat(vatRate);
  const vat = net * rate / 100;
  const grand = net + vat;
  return (
    <div className={`bg-[#F7F9F0] border border-[#EAF0DD] rounded-xl px-5 py-4 ${className}`}>
      <div className="flex items-baseline justify-between gap-6">
        <span className="font-['Metropolis'] text-[11px] font-bold tracking-[1.5px] uppercase text-[#51534A]">Subtotal (ex-VAT)</span>
        <span className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">{formatGBP(net)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-6 mt-2">
        <span className="font-['Metropolis'] text-[11px] font-bold tracking-[1.5px] uppercase text-[#51534A]">VAT ({rate}%)</span>
        <span className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">{formatGBP(vat)}</span>
      </div>
      <div className="flex items-baseline justify-between gap-6 mt-3 pt-3 border-t border-[#DDE6C9]">
        <span className="font-['Metropolis'] text-[11px] font-bold tracking-[1.5px] uppercase text-[#3a3b33]">Grand total (inc-VAT)</span>
        <span className="font-['Metropolis'] text-lg font-extrabold text-[#00713f]">{formatGBP(grand)}</span>
      </div>
    </div>
  );
};

const DEADLINE_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// "4 Jun 2026, 17:00" – British, no seconds. Used for the deadline +
// last-saved lines on the editable/locked submitted states.
const formatDeadline = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${d.getDate()} ${DEADLINE_MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`;
};

// Human "time remaining" to a deadline given ms left. Days/hours at a
// distance; minutes inside the final hour (for the within-the-hour
// countdown). Returns null when the deadline has passed.
const formatTimeRemaining = (msLeft) => {
  if (msLeft == null || msLeft <= 0) return null;
  const mins = Math.floor(msLeft / 60000);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} remaining`;
  const days = Math.ceil(msLeft / 86400000);
  if (days <= 1) {
    const hrs = Math.floor(msLeft / 3600000);
    return `${hrs} hour${hrs === 1 ? '' : 's'} remaining`;
  }
  return `${days} days remaining`;
};

// Date-picker round-trip helpers. The form stores deadlines as
// strings ("YYYY-MM-DDTHH:mm") so the DB write is straightforward;
// the picker takes Date objects so we convert on render/onChange.
// Mirrors the toDate/fromDate pair in AddBrief.js – the admin uses
// the same picker, and consistency between the two flows keeps the
// supplier and Fusion experience aligned.
const toDate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fromDate = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Qty defaults to '1' so the placeholder "1" actually behaves like a
// default – suppliers who only type a unit price still get a working
// line total without remembering to fill quantity.
const emptyLineItem = (sortOrder = 0) => ({
  description: '',
  qty: '1',
  unit_price: '',
  line_total: '',
  sort_order: sortOrder,
});

// Structured deposit-trigger vocabulary (quote-comparison brief §3). Stored
// as the snake_case key; these labels render it on the form + compare screen.
export const DEPOSIT_TRIGGER_LABEL = {
  on_booking: 'on booking',
  on_order: 'on order',
  other: 'other',
};

// Cheap content fingerprint for the autosave dirty-check. Only fields the
// supplier can edit are included so loading a fresh response and saving it
// back doesn't churn the network.
const computeStateHash = (form, itemised, vat, lines, atts) => JSON.stringify({
  d: form.deadline_date,
  p: form.payment_schedule,
  s: form.salient_points,
  t: form.total_price,
  c: form.comments,
  dp: form.deposit_pct,
  dt: form.deposit_trigger,
  bt: form.balance_terms,
  pp: form.penalty_pct_per_day,
  i: itemised,
  v: vat,
  l: lines.map(li => `${li.description}|${li.qty}|${li.unit_price}|${li.line_total}`),
  a: atts.map(a => `${a.file_url}|${a.file_name}|${a.file_size_bytes ?? ''}`),
});

const formatSavedAgo = (savedAt) => {
  if (!savedAt) return '';
  const sec = Math.max(0, Math.round((Date.now() - savedAt.getTime()) / 1000));
  if (sec < 5) return 'Saved just now';
  if (sec < 60) return `Saved ${sec}s ago`;
  if (sec < 3600) return `Saved ${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `Saved ${Math.floor(sec / 3600)}h ago`;
  // Build #18 §8a: roll hours → days → weeks → months so a long-idle
  // draft reads "Saved 22 days ago", not "Saved 550h ago".
  const days = Math.floor(sec / 86400);
  if (days < 7) return `Saved ${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 35) { const w = Math.floor(days / 7); return `Saved ${w} week${w === 1 ? '' : 's'} ago`; }
  const months = Math.floor(days / 30);
  return `Saved ${months} month${months === 1 ? '' : 's'} ago`;
};

export default function SupplierResponse() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [brief, setBrief] = useState(null);
  const [recipient, setRecipient] = useState(null);
  const [existingResponse, setExistingResponse] = useState(null);
  // §5 – count of clarifications published AFTER this supplier submitted, lifted
  // from BriefQuestions so the top-of-page amber alert can warn their quote may
  // now be priced against stale scope.
  const [newClarCount, setNewClarCount] = useState(0);
  // Dismisses the top-of-page "new clarifications" alert once the supplier has
  // actually gone to read them (clicked through). Session-scoped – the banner
  // is a nudge to read, so once they've been sent there it has done its job.
  const [clarBannerDismissed, setClarBannerDismissed] = useState(false);
  // Brief-amendment indicators: the material diff since this supplier last saw
  // the brief, the note Fusion sent, and whether their submitted response was
  // reopened by a material amendment.
  const [amendDiff, setAmendDiff] = useState(null);
  const [amendedAt, setAmendedAt] = useState(null);
  const [amendNote, setAmendNote] = useState(null);
  // The brief as the supplier last saw it – current brief row with its
  // amendable fields rewound to the last-seen version snapshot. Threaded
  // into SupplierBriefFactsPanel so each changed fact renders with an
  // amber border + "Changed" chip + struck-through previous value.
  const [previousBrief, setPreviousBrief] = useState(null);
  const [amendedPending, setAmendedPending] = useState(false);
  const [supplierUser, setSupplierUser] = useState(null);
  // Primary owner (briefs.owner_id) hydrated alongside the brief so the
  // next-steps panel can name the PM. Null when no owner is on file or
  // when the owner is inactive – the FRESH/ACKNOWLEDGED variants below
  // degrade to the no-PM fallback copy in that case. Spec point 6:
  // primary owner ONLY – brief_owners (secondary list) is not surfaced
  // on the supplier side.
  const [primaryOwner, setPrimaryOwner] = useState(null);
  // Captures whether the supplier already acknowledged this award before
  // THIS load. The acknowledgement UPSERT below stamps acknowledged_at
  // on first open, so without this snapshot we'd never render the FRESH
  // variant – the same load that fires the UPSERT would always read as
  // acknowledged. Sequenced strictly: SELECT prior state → set flag →
  // THEN fire-and-forget UPSERT.
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  // Deadline-extension request (supplier asks for more time; no date proposed).
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extensionNote, setExtensionNote] = useState('');
  const [extensionSubmitting, setExtensionSubmitting] = useState(false);
  // declineReasonCategory was used by the legacy two-radio picker
  // (Budget too low / Don't have capacity). The picker was retired
  // in the 2026-06 decline-brief simplification – now every new
  // decline writes decline_reason_category='free_text' direct. The
  // state hook stays so legacy "reset" callsites still compile;
  // eslint-disable below silences the unused-write warning.
  // eslint-disable-next-line no-unused-vars
  const [declineReasonCategory, setDeclineReasonCategory] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Multi-file attachments. Each item: { file_url, file_name,
  // file_size_bytes, mime_type, uploaded_at, id?, persisted? }.
  // `persisted: true` marks rows already in brief_response_attachments
  // (only relevant when re-loading an existing submitted/draft response).
  const [attachments, setAttachments] = useState([]);
  // Brief-level supporting documents (Fusion-uploaded, read-only to the
  // supplier). Pulled separately from briefs.* in the loader.
  const [briefAttachments, setBriefAttachments] = useState([]);
  // Supporting-document viewer dialog. null = closed; an object with
  // { url, name } when open. Replaces the previous "View opens in a
  // new tab" behaviour – keeps suppliers inside the brief context
  // and works on mobile where popups are unreliable.
  const [viewerDoc, setViewerDoc] = useState(null);
  const [dragging, setDragging] = useState(false);
  // Quote shape – supplier can either submit a single bottom-line total or
  // break the quote into line items. When itemised, total_price stored on the
  // brief_response = sum of line_totals (the NET subtotal). vat_rate is stored
  // alongside so internal viewers can derive gross.
  const [isItemised, setIsItemised] = useState(false);
  const [vatRate, setVatRate] = useState('20');
  const [lineItems, setLineItems] = useState([emptyLineItem(0)]);
  const [form, setForm] = useState({
    deadline_date: '',
    payment_schedule: '',
    salient_points: '',
    total_price: '',
    comments: '',
    // Structured payment terms (§3) – compared as values on the compare
    // screen; payment_schedule stays for anything that doesn't fit.
    deposit_pct: '',
    deposit_trigger: '',
    balance_terms: '',
    penalty_pct_per_day: '',
  });
  // Autosave + review-modal state
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  // Submission-confirmation brief §1 – success modal shown after the FIRST
  // submit (not on re-saves, which stay inline).
  const [submitSuccess, setSubmitSuccess] = useState(false);
  // Edit-after-submit: a submitted response is editable until the
  // deadline. editingSubmitted flips the submitted summary into the
  // reused response form; nowTs ticks once a second while submitted so
  // the deadline countdown is live and the lock fires reliably the
  // moment the deadline passes.
  const [editingSubmitted, setEditingSubmitted] = useState(false);
  // Awarded-brief celebration-vs-receipt (awarded-brief-state brief §1/§2).
  // First VIEW of an awarded brief by THIS portal user shows the celebration;
  // every visit after shows the compact receipt. Tracked per-user-per-brief in
  // localStorage – fires on first view (not on award, so a fortnight-later
  // login still celebrates), consumed once, never replays. Defaults to the
  // receipt so a returning user never flashes the celebration.
  const [awardCelebrationSeen, setAwardCelebrationSeen] = useState(true);
  const [nowTs, setNowTs] = useState(() => Date.now());
  // Minute-cadence clock for the sticky-bar deadline countdown while editing –
  // nowTs above only ticks for the submitted state, so the countdown needs its
  // own live clock (deadline-reminder §4/§5).
  const barNow = useNow();
  // eslint-disable-next-line no-unused-vars
  const [savedAgoTick, setSavedAgoTick] = useState(0); // forces re-render so "Saved Xs ago" advances
  const responseIdRef = useRef(null);
  const lastSavedHashRef = useRef('');
  // Account-menu slideout – header matches the rest of the supplier
  // portal (pill on the right rather than a back link) so suppliers
  // can reach Your details / Help from any portal page.
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [slideoutTab, setSlideoutTab] = useState('profile');
  const openSlideout = (tab) => { setSlideoutTab(tab); setSlideoutOpen(true); };

  useEffect(() => {
    const getData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: su } = await supabase.from('supplier_users').select('*, suppliers(*)').eq('id', user.id).single();
      setSupplierUser(su);
      // Brief + primary owner (briefs.owner_id). Owner is embedded so we
      // can name the PM in the next-steps panel below without a second
      // round-trip. is_active flows through so inactive owners degrade
      // cleanly to the no-PM fallback. Spec point 6: primary owner ONLY
      // – brief_owners (secondary list) is intentionally not joined.
      const { data: b } = await supabase
        .from('briefs')
        .select('*, owner:internal_users!owner_id(id, full_name, email, is_active)')
        .eq('id', id)
        .single();
      // Suppliers can reach a brief in 'sent' or 'closed' state: 'sent' is the
      // live response window; 'closed' covers awarded + lost briefs which still
      // need to be viewable from the Awarded/Closed tabs. Anything else (drafts,
      // reverts) bounces. RLS blocks reads at the DB; this is defence in depth.
      if (!b || (b.status !== 'sent' && b.status !== 'closed')) {
        navigate('/portal');
        return;
      }
      setBrief(b);
      // Sanitised PM contact for the awarded next-steps panel. Drops the
      // owner entirely when inactive or unnamed so the FRESH variant
      // routes through its no-PM copy rather than rendering an empty
      // strong-tag. Name is run through extractName so a legacy
      // ", Title" suffix is stripped before it reaches the supplier.
      if (b.owner && b.owner.is_active !== false) {
        const ownerName = extractName(b.owner.full_name);
        if (ownerName) {
          setPrimaryOwner({
            name: ownerName,
            firstName: ownerName.split(' ')[0],
            email: b.owner.email || null,
          });
        }
      }
      // Fusion-uploaded supporting docs visible to the supplier on the
      // brief. Fail-soft: if the query errors (e.g. table absent in older
      // environments), we just show no attachments rather than blocking.
      const { data: briefAtts } = await supabase
        .from('brief_attachments')
        .select('*')
        .eq('brief_id', id)
        .order('uploaded_at', { ascending: true });
      setBriefAttachments(briefAtts || []);
      const { data: r } = await supabase.from('brief_recipients').select('*').eq('brief_id', id).eq('supplier_id', su.supplier_id).maybeSingle();
      setRecipient(r);
      // Opening the brief IS the supplier looking at what changed, so stamp
      // it – the dashboard's changed-brief alert and What's New entry both
      // suppress anything older than this. Deliberately NOT clearing
      // amend_pending_since: an amendment stays outstanding until they
      // actually resubmit, which is what the CHANGED pills below rely on.
      //
      // Fire-and-forget and error-tolerant on purpose: this is a read-receipt,
      // and it must never be able to stop a supplier opening a brief (it also
      // no-ops harmlessly until 2026-08-brief-changes-seen.sql is run).
      if (r?.id) {
        const seenIso = new Date().toISOString();
        // Local copy first, so the dashboard clears even if the write below is
        // refused. Swallowing that error is what let this fail invisibly the
        // first time round – it now says so in the console.
        markChangesSeen(r.id, seenIso);
        supabase.from('brief_recipients')
          .update({ changes_seen_at: seenIso })
          .eq('id', r.id)
          .then(({ error }) => {
            if (error) console.warn('changes_seen_at not saved:', error.message, '– falling back to this device only.');
          }, (e) => {
            console.warn('changes_seen_at not saved:', e?.message || e);
          });
      }
      // Amendment indicators – the MATERIAL change since the version this
      // supplier last saw (silent edits are excluded from the diff path).
      const { data: bv } = await supabase.from('brief_versions').select('*').eq('brief_id', id).order('version_number', { ascending: true });
      const versions = bv || [];
      const material = versions.filter(v => !v.silent);
      const latestMaterial = material.length ? material[material.length - 1] : null;
      const lastSeen = r?.last_seen_version || 1;
      const seenSnap = versions.find(v => v.version_number === lastSeen) || versions[0] || null;
      if (latestMaterial && seenSnap && lastSeen < latestMaterial.version_number) {
        const d = diffBriefVersions(seenSnap, latestMaterial);
        if (hasBriefChanges(d)) {
          setAmendDiff(d);
          setAmendNote(latestMaterial.note_to_suppliers || null);
          // When it was amended – the changes view dates the change so the
          // supplier can tell "since I responded" from "months ago".
          setAmendedAt(latestMaterial.created_at || null);
          // Build a "previous brief" view by rewinding the live brief's
          // amendable fields to the last-seen snapshot. SupplierBriefFactsPanel
          // diffs current vs previous by fact key to render the per-fact
          // amber markers and struck-through old values.
          setPreviousBrief({
            ...b,
            brief_name: seenSnap.brief_name,
            fusion_project_name: seenSnap.fusion_project_name,
            client: seenSnap.client,
            required_response_time: seenSnap.required_response_time,
            expected_required_by_date: seenSnap.expected_required_by_date,
            delivery_access_to_date: seenSnap.delivery_access_to_date,
            event_live_date: seenSnap.event_live_date,
            event_live_end_date: seenSnap.event_live_end_date,
            location: seenSnap.location,
            maximum_budget: seenSnap.maximum_budget,
            brief_content: seenSnap.brief_content,
          });
        }
      }
      // 2026-06 amend-flow: last_seen_version DOES NOT bump on open
      // anymore – that previously caused the "CHANGED" highlights to
      // disappear after a single view. Now the bump fires inside the
      // response-submit handler instead, so highlights persist across
      // reopens until the supplier has acted on the amendment. Open-
      // alone is no longer "I've reviewed this"; submit is.
      // First-open stamp – only update if currently null so the timestamp
      // captures when the supplier *first* saw the brief, not the most
      // recent visit. Fire-and-forget; not worth blocking the page on.
      if (r && !r.viewed_at) {
        supabase
          .from('brief_recipients')
          .update({ viewed_at: new Date().toISOString() })
          .eq('id', r.id)
          .is('viewed_at', null)
          .then(() => {});
      }
      // Award acknowledgement – opening the awarded brief = acknowledging
      // it. Drives the supplier portal's four award-visibility cues
      // (welcome panel, callout strip, fresh card, tab pulse), all of
      // which resolve once this row exists.
      //
      // Sequence is load-bearing: READ the existing acknowledgement row
      // BEFORE writing so the next-steps panel below can pick FRESH vs
      // ACKNOWLEDGED based on the PRIOR state. If we keyed off the
      // post-UPSERT state, the panel would always render acknowledged
      // (because this same load just stamped acknowledged_at). The
      // SELECT awaits; the UPSERT remains fire-and-forget.
      //
      // ignoreDuplicates makes the write idempotent: the first open
      // creates the row; later opens hit ON CONFLICT DO NOTHING so
      // acknowledged_at stays frozen at its original timestamp.
      if (b.status === 'closed' && b.awarded_supplier_id === su.supplier_id) {
        // A SELECT stood here reading acknowledged_at into a
        // `wasPreviouslyAcknowledged` state – presumably towards a "first time
        // you've seen this" treatment on the award. Nothing ever read the
        // state, so the query was work done for nobody. Removed 2026-08-14; the
        // idempotent upsert below still freezes acknowledged_at at the first
        // open, so the data needed to build that treatment is all still there.
        supabase
          .from('supplier_award_acknowledgements')
          .upsert(
            {
              supplier_id: su.supplier_id,
              brief_id: b.id,
              awarded_at: b.closed_at || new Date().toISOString(),
              acknowledged_at: new Date().toISOString(),
            },
            { onConflict: 'supplier_id,brief_id', ignoreDuplicates: true }
          )
          .then(() => {});
      }
      // maybeSingle, not single: a supplier viewing a brief they haven't
      // responded to yet has NO brief_responses row, and .single() returns a
      // 406 "Not Acceptable" (PostgREST rejects 0 rows for object+json).
      // maybeSingle returns null cleanly and the `if (res)` guard handles it.
      const { data: res } = await supabase.from('brief_responses').select('*').eq('brief_id', id).eq('supplier_id', su.supplier_id).maybeSingle();
      if (res) {
        setExistingResponse(res);
        setAmendedPending(!!res.amended_pending);
        responseIdRef.current = res.id;
        if (res.last_autosaved_at) setLastSavedAt(new Date(res.last_autosaved_at));
        setForm({
          // Stored as a string so writes are straightforward. The
          // picker bindings convert string ↔ Date on render. Raw DB
          // value flows in here; toDate handles the timezone-suffix
          // form Postgres returns for timestamptz.
          deadline_date: res.deadline_date || '',
          payment_schedule: res.payment_schedule || '',
          salient_points: res.salient_points || '',
          total_price: res.total_price || '',
          comments: res.comments || '',
          deposit_pct: res.deposit_pct ?? '',
          deposit_trigger: res.deposit_trigger || '',
          balance_terms: res.balance_terms || '',
          penalty_pct_per_day: res.penalty_pct_per_day ?? '',
        });
        setIsItemised(!!res.is_itemised);
        if (res.vat_rate != null) setVatRate(String(clampVat(res.vat_rate)));
        if (res.is_itemised) {
          const { data: li } = await supabase
            .from('brief_response_line_items')
            .select('*')
            .eq('response_id', res.id)
            .order('sort_order', { ascending: true });
          if (li && li.length > 0) {
            // Always derive line_total from qty * unit_price on load so
            // legacy draft rows that were saved with null line_total
            // (pre-default-qty fix) still display a working subtotal.
            setLineItems(li.map((row, idx) => {
              const qty = row.qty != null ? String(row.qty) : '1';
              const unit = row.unit_price != null ? String(row.unit_price) : '';
              const qNum = Number(qty);
              const pNum = Number(unit);
              const lt = (Number.isFinite(qNum) && Number.isFinite(pNum) && unit !== '')
                ? (qNum * pNum).toFixed(2)
                : '';
              return {
                description: row.description || '',
                qty,
                unit_price: unit,
                line_total: lt,
                sort_order: row.sort_order ?? idx,
              };
            }));
          }
        }
        // Pull any attachments. Pre-Phase-B responses won't have rows here
        // – fall back to the legacy single-doc field so old submissions
        // still display their attached file.
        const { data: atts } = await supabase
          .from('brief_response_attachments')
          .select('*')
          .eq('response_id', res.id)
          .order('uploaded_at', { ascending: true });
        if (atts && atts.length > 0) {
          setAttachments(atts.map(a => ({
            id: a.id,
            file_url: a.file_url,
            file_name: a.file_name,
            file_size_bytes: a.file_size_bytes,
            mime_type: a.mime_type,
            uploaded_at: a.uploaded_at,
            persisted: true,
          })));
        } else if (res.supporting_doc_url) {
          setAttachments([{
            file_url: res.supporting_doc_url,
            file_name: res.supporting_doc_name || 'Attached document',
            file_size_bytes: null,
            mime_type: null,
            uploaded_at: res.supporting_doc_uploaded_at || null,
            legacy: true,
          }]);
        }
      }
      setLoading(false);
    };
    getData();
  }, [id, navigate]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Line-item edits auto-recompute line_total = qty * unit_price. Blank inputs
  // are tolerated mid-edit – line_total just collapses back to '' until both
  // sides are numeric again.
  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => {
      if (i !== idx) return li;
      const next = { ...li, [field]: value };
      if (field === 'qty' || field === 'unit_price') {
        // Blank qty falls back to 1 so the placeholder semantics hold
        // even if the supplier clears the field.
        const qRaw = next.qty === '' ? '1' : next.qty;
        const q = Number(qRaw);
        const p = Number(next.unit_price);
        next.line_total = (Number.isFinite(q) && Number.isFinite(p) && next.unit_price !== '')
          ? (q * p).toFixed(2)
          : '';
      }
      return next;
    }));
  };
  const addLineItem = () => setLineItems(prev => [...prev, emptyLineItem(prev.length)]);
  const removeLineItem = (idx) => setLineItems(prev => {
    const next = prev.filter((_, i) => i !== idx).map((li, i) => ({ ...li, sort_order: i }));
    return next.length === 0 ? [emptyLineItem(0)] : next;
  });

  // Net subtotal = the supplier's ex-VAT figure (sum of line_totals when
  // itemised, otherwise the single total_price input). Stored on
  // brief_responses.total_price going forward; vat_rate stored alongside.
  const netSubtotal = useMemo(() => {
    if (isItemised) {
      return lineItems.reduce((sum, li) => sum + (Number(li.line_total) || 0), 0);
    }
    return Number(form.total_price) || 0;
  }, [isItemised, lineItems, form.total_price]);
  // VAT + grand total are derived inside the shared ResultPanel (BRIEF-25 §1)
  // from netSubtotal + vatRate, so the three money surfaces can't drift.

  // Append an immutable version snapshot. version_number is the next in
  // sequence for this (brief, supplier). Best-effort: logs but never throws,
  // so a snapshot failure can't block the supplier's save.
  const appendVersionSnapshot = async (responseId, snap) => {
    try {
      const { count } = await supabase
        .from('brief_response_versions')
        .select('id', { count: 'exact', head: true })
        .eq('brief_id', id)
        .eq('supplier_id', supplierUser.supplier_id);
      const { error: snapErr } = await supabase.from('brief_response_versions').insert([{
        response_id: responseId,
        brief_id: id,
        supplier_id: supplierUser.supplier_id,
        version_number: (count || 0) + 1,
        saved_at: snap.saved_at,
        is_itemised: snap.is_itemised,
        vat_rate: snap.vat_rate,
        total_price: snap.total_price,
        deadline_date: snap.deadline_date,
        payment_schedule: snap.payment_schedule,
        salient_points: snap.salient_points,
        comments: snap.comments,
        line_items: snap.line_items,
        document_refs: snap.document_refs,
      }]);
      if (snapErr) console.warn('Version snapshot insert failed', snapErr);
    } catch (e) {
      console.warn('Version snapshot failed', e);
    }
  };

  // Backfill the pre-edit state as version 1 for a response submitted before
  // versioning existed (no snapshots yet). Without this, the first edit of a
  // legacy response would capture only the post-edit state, leaving nothing
  // to diff against. Reads the current DB row + children (the last-saved
  // version) and snapshots them BEFORE the new write lands. No-op once any
  // version exists.
  const ensureOriginalSnapshot = async (responseId) => {
    if (!responseId) return;
    const { count } = await supabase
      .from('brief_response_versions')
      .select('id', { count: 'exact', head: true })
      .eq('brief_id', id)
      .eq('supplier_id', supplierUser.supplier_id);
    if ((count || 0) > 0) return;
    const { data: row } = await supabase.from('brief_responses').select('*').eq('id', responseId).single();
    if (!row) return;
    const [{ data: li }, { data: att }] = await Promise.all([
      supabase.from('brief_response_line_items').select('*').eq('response_id', responseId),
      supabase.from('brief_response_attachments').select('*').eq('response_id', responseId),
    ]);
    await appendVersionSnapshot(responseId, {
      saved_at: row.submitted_at || row.last_autosaved_at || new Date().toISOString(),
      is_itemised: row.is_itemised,
      vat_rate: Number(row.vat_rate) || 0,
      total_price: row.total_price,
      deadline_date: row.deadline_date,
      payment_schedule: row.payment_schedule,
      salient_points: row.salient_points,
      comments: row.comments,
      line_items: (li || [])
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map(x => ({ description: x.description, qty: x.qty, unit_price: x.unit_price, line_total: x.line_total, sort_order: x.sort_order })),
      document_refs: (att || []).map(a => ({ file_url: a.file_url, file_name: a.file_name, file_size_bytes: a.file_size_bytes })),
    });
  };

  // Shared writer used by both autosave (submitted:false → draft) and final
  // submission (submitted:true). Wipes-and-rewrites child rows so the snapshot
  // on disk always reflects current state – simple and idempotent.
  const writeResponseData = async ({ submitted }) => {
    if (!supplierUser || !brief) return null;
    // Closed-brief lockdown (§2): a closed brief accepts no writes – draft
    // autosave OR submit. Client belt-and-braces; the server enforces it too.
    if (brief.status === 'closed') return null;
    const now = new Date();
    // Empty-string → null for the timestamp column so Postgres doesn't
    // reject the write with "invalid input syntax for type timestamp".
    // Without this normalisation, every save where the supplier hasn't
    // filled in a deadline silently fails (the column was previously
    // populated from a form input that defaults to '').
    const updates = {
      ...form,
      deadline_date: form.deadline_date && form.deadline_date.trim() !== ''
        ? form.deadline_date
        : null,
      // Structured payment terms (§3): '' → null so the numeric columns
      // don't reject the write; trim the text fields.
      deposit_pct: form.deposit_pct !== '' && form.deposit_pct != null ? Number(form.deposit_pct) : null,
      penalty_pct_per_day: form.penalty_pct_per_day !== '' && form.penalty_pct_per_day != null ? Number(form.penalty_pct_per_day) : null,
      deposit_trigger: form.deposit_trigger || null,
      balance_terms: form.balance_terms && form.balance_terms.trim() !== '' ? form.balance_terms.trim() : null,
      total_price: netSubtotal || null,
      is_itemised: isItemised,
      vat_rate: clampVat(vatRate),
      status: submitted ? 'submitted' : 'draft',
      is_submitted: submitted,
      last_autosaved_at: now.toISOString(),
      supporting_doc_url: null,
      supporting_doc_name: null,
      supporting_doc_uploaded_at: null,
      ...(submitted ? { submitted_at: now } : {}),
    };
    let responseId = responseIdRef.current;
    if (responseId) {
      const { error: updateErr } = await supabase.from('brief_responses').update(updates).eq('id', responseId);
      // Surface UPDATE errors – previously this path silently swallowed
      // failures, so a rejected write looked exactly like a successful
      // one to the caller. Returning null here funnels into the same
      // error path the INSERT branch already uses.
      if (updateErr) {
        setError(`Could not save: ${updateErr.message}`);
        setTimeout(() => setError(null), 6000);
        return null;
      }
    } else {
      const { data: created, error: createErr } = await supabase
        .from('brief_responses')
        .insert([{ brief_id: id, supplier_id: supplierUser.supplier_id, ...updates }])
        .select('*')
        .single();
      if (createErr || !created) {
        if (createErr) {
          setError(`Could not save: ${createErr.message}`);
          setTimeout(() => setError(null), 6000);
        }
        return null;
      }
      responseId = created.id;
      responseIdRef.current = responseId;
      setExistingResponse(created);
    }

    // Attachments: INSERT the new set first, then delete the rows it replaces.
    //
    // This used to delete-then-insert with no error check on either call. If
    // the insert failed for any reason the delete had already run, so the
    // supplier's attachments silently vanished – they'd saved, seen no error,
    // and lost the files off the response. Inserting first means a failure
    // leaves the previous set exactly where it was, and it now reports itself
    // instead of failing quietly.
    const { data: priorAtts } = await supabase
      .from('brief_response_attachments')
      .select('id')
      .eq('response_id', responseId);
    const priorAttIds = (priorAtts || []).map(r => r.id);
    if (attachments.length > 0) {
      const attRows = attachments.map(a => ({
        response_id: responseId,
        file_url: a.file_url,
        file_name: a.file_name,
        file_size_bytes: a.file_size_bytes,
        mime_type: a.mime_type,
      }));
      const { error: attErr } = await supabase.from('brief_response_attachments').insert(attRows);
      if (attErr) {
        setError(`Your attachments couldn't be saved: ${attErr.message}. Nothing else has been changed – please try again.`);
        setSaving(false);
        return null;
      }
    }
    if (priorAttIds.length > 0) {
      await supabase.from('brief_response_attachments').delete().in('id', priorAttIds);
    }

    await supabase.from('brief_response_line_items').delete().eq('response_id', responseId);
    if (isItemised) {
      const filledLines = lineItems.filter(li =>
        (li.description && li.description.trim() !== '') ||
        Number(li.qty) > 0 ||
        Number(li.unit_price) > 0
      );
      if (filledLines.length > 0) {
        const lineRows = filledLines.map((li, idx) => ({
          response_id: responseId,
          description: li.description?.trim() || null,
          qty: li.qty === '' ? null : Number(li.qty),
          unit_price: li.unit_price === '' ? null : Number(li.unit_price),
          line_total: li.line_total === '' ? null : Number(li.line_total),
          sort_order: idx,
        }));
        // Same silent-failure class as the attachments above – an itemised
        // breakdown that fails to insert leaves the quote with a total and no
        // lines behind it, and the supplier is told nothing.
        const { error: lineErr } = await supabase.from('brief_response_line_items').insert(lineRows);
        if (lineErr) {
          setError(`Your itemised breakdown couldn't be saved: ${lineErr.message}. Please try again.`);
          setSaving(false);
          return null;
        }
      }
    }

    // Snapshot an immutable version on every SUBMIT (drafts/autosaves never
    // version). Append-only: version 1 is the original submission, 2+ are
    // edits. The admin edit-trail / diff view and the "Edited N times" row
    // marker read these. Built from in-memory state, which mirrors what we
    // just wrote.
    if (submitted) {
      const versionLineItems = isItemised
        ? lineItems
            .filter(li =>
              (li.description && li.description.trim() !== '') ||
              Number(li.qty) > 0 ||
              Number(li.unit_price) > 0
            )
            .map((li, idx) => ({
              description: li.description?.trim() || null,
              qty: li.qty === '' ? null : Number(li.qty),
              unit_price: li.unit_price === '' ? null : Number(li.unit_price),
              line_total: li.line_total === '' ? null : Number(li.line_total),
              sort_order: idx,
            }))
        : [];
      await appendVersionSnapshot(responseId, {
        saved_at: now.toISOString(),
        is_itemised: isItemised,
        vat_rate: clampVat(vatRate),
        total_price: netSubtotal || null,
        deadline_date: updates.deadline_date,
        payment_schedule: form.payment_schedule || null,
        salient_points: form.salient_points || null,
        comments: form.comments || null,
        deposit_pct: updates.deposit_pct,
        deposit_trigger: updates.deposit_trigger,
        balance_terms: updates.balance_terms,
        penalty_pct_per_day: updates.penalty_pct_per_day,
        line_items: versionLineItems,
        document_refs: attachments.map(a => ({
          file_url: a.file_url,
          file_name: a.file_name,
          file_size_bytes: a.file_size_bytes ?? null,
        })),
      });
    }

    return responseId;
  };

  // Autosaver. Bails when nothing changed since last save, when the response
  // is already submitted, or when the supplier isn't in the editable state.
  // Latest closure is exposed via saveDraftRef so the interval can call it
  // without being torn down on every render.
  const saveDraft = async () => {
    if (!recipient || recipient.status !== 'accepted') return;
    if (existingResponse?.is_submitted) return;
    const currentHash = computeStateHash(form, isItemised, vatRate, lineItems, attachments);
    if (currentHash === lastSavedHashRef.current) return;
    setIsAutoSaving(true);
    try {
      const responseId = await writeResponseData({ submitted: false });
      if (responseId) {
        lastSavedHashRef.current = currentHash;
        setLastSavedAt(new Date());
      }
    } finally {
      setIsAutoSaving(false);
    }
  };
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  // Seed the hash baseline once the initial load settles so the very first
  // autosave tick after page open doesn't fire a no-op write.
  useEffect(() => {
    if (!loading) {
      lastSavedHashRef.current = computeStateHash(form, isItemised, vatRate, lineItems, attachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Autosave every 30s – only does work when something has changed.
  useEffect(() => {
    const t = setInterval(() => { saveDraftRef.current?.(); }, 30000);
    return () => clearInterval(t);
  }, []);

  // Tick the "Saved Xs ago" indicator every 5s.
  useEffect(() => {
    const t = setInterval(() => setSavedAgoTick(x => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Auto-dismiss the "response sent" toast (§4).
  useEffect(() => {
    if (!submitSuccess) return undefined;
    const t = setTimeout(() => setSubmitSuccess(false), 6000);
    return () => clearTimeout(t);
  }, [submitSuccess]);

  // Review-modal Escape → return to EDITING, never submit (§7).
  useEffect(() => {
    if (!showReviewModal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowReviewModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showReviewModal]);

  // First-view detection for the awarded celebration (§2). Runs once the brief
  // + user are loaded: if this user hasn't seen this brief's award yet, show
  // the celebration this visit and immediately consume the flag.
  useEffect(() => {
    if (!supplierUser?.id || !id) return;
    if (brief?.status !== 'closed' || brief?.awarded_supplier_id !== supplierUser?.supplier_id) return;
    const key = `sp_award_seen_${supplierUser.id}_${id}`;
    try {
      if (!localStorage.getItem(key)) {
        setAwardCelebrationSeen(false);
        localStorage.setItem(key, '1');
      }
    } catch { /* localStorage unavailable → default to the receipt */ }
  }, [supplierUser?.id, supplierUser?.supplier_id, id, brief?.status, brief?.awarded_supplier_id]);

  // Deadline clock – ticks once a second only while the response is
  // submitted, so the editable/locked states flip exactly at the
  // deadline and the within-the-hour countdown is live. No timer runs
  // for pending/accepted/declined states.
  const isSubmittedForClock = existingResponse?.is_submitted || recipient?.status === 'responded';
  useEffect(() => {
    if (!isSubmittedForClock) return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isSubmittedForClock]);

  // Save & Close is an explicit user action, so it bypasses the autosave
  // dirty-check (which bails when the hash matches the last autosave).
  // Otherwise a click immediately after an autosave tick would feel like
  // a no-op even when the underlying write completed fine.
  const handleSaveAndClose = async () => {
    if (!recipient || recipient.status !== 'accepted' || existingResponse?.is_submitted) {
      navigate('/portal');
      return;
    }
    setSaving(true);
    let saved = false;
    try {
      const responseId = await writeResponseData({ submitted: false });
      if (responseId) {
        lastSavedHashRef.current = computeStateHash(form, isItemised, vatRate, lineItems, attachments);
        setLastSavedAt(new Date());
        saved = true;
      }
    } finally {
      setSaving(false);
    }
    // Only navigate away on a clean save – otherwise leave the user on
    // the form so they can see the inline error and try again. Before
    // this, the page navigated home regardless of write outcome, which
    // looked exactly like a successful save even when nothing persisted.
    if (saved) navigate('/portal');
  };

  const handleAccept = async () => {
    setSaving(true);
    const acceptedAt = new Date().toISOString();
    await supabase
      .from('brief_recipients')
      .update({ status: 'accepted', accepted_at: acceptedAt })
      .eq('id', recipient.id);
    setRecipient(prev => ({ ...prev, status: 'accepted', accepted_at: acceptedAt }));
    // Notify owners (respects the per-brief mute). The status write above
    // is independent of the email, so a muted brief still records the accept.
    const supplierName = await supplierNameForEmail();
    const briefUrl = `${window.location.origin}/briefs/${id}`;
    await notifyOwners((ownerName) => supplierAcceptedEmail({
      ownerName,
      supplierName,
      briefName: brief?.brief_name,
      projectNumber: brief?.fusion_project_number,
      client: brief?.client,
      projectName: brief?.fusion_project_name,
      briefUrl,
    }));
    setSaving(false);
  };

  // Decline reasons: 2026-06 simplification.
  //
  // The earlier two-radio picker ("Budget too low" / "Don't have
  // capacity") was dropped in favour of a single mandatory free-text
  // box. The radios funnelled most declines into two buckets that
  // gave Fusion almost no actionable insight; the new form forces
  // the supplier to articulate their reason in their own words.
  //
  // decline_reason_category column is retained in the DB so legacy
  // rows ('price_point_low', 'out_of_capacity', 'other', 'free_text')
  // keep rendering correctly on the admin side. New declines write
  // 'free_text' as the canonical category – the real signal lives
  // in decline_reason.
  //
  // Minimum-length gate (15 chars after trim) blocks "x" / "no" /
  // placeholder junk while still allowing a genuine short reason
  // like "budget too low for scope" (24 chars). Short enough to be
  // permissive of real answers, long enough to deter spam.
  const DECLINE_REASON_MIN_CHARS = 15;
  // Shared so the Decline tooltip reads identically on the brief-detail
  // (pending) entry and inside the response form (per the info-icons brief).
  const DECLINE_INFO = {
    heading: 'Declining this brief',
    body: "Lets Fusion 2K know you won't be quoting. You'll be asked for a brief reason, then it moves out of your active briefs. You can change your mind and respond any time up to the response deadline – after that, it's final.",
  };
  const handleDeclineConfirm = async () => {
    const note = (declineReason || '').trim();
    if (note.length < DECLINE_REASON_MIN_CHARS) {
      setError(`Please write at least ${DECLINE_REASON_MIN_CHARS} characters – this helps us refine future briefs.`);
      return;
    }
    setSaving(true);
    setError(null);
    await supabase.from('brief_recipients').update({
      status: 'declined',
      decline_reason: note,
      decline_reason_category: 'free_text',
      declined_at: new Date().toISOString(),
    }).eq('id', recipient.id);
    // Notify owners (respects the per-brief mute). The decline is recorded
    // above regardless of whether the email goes out.
    const supplierName = await supplierNameForEmail();
    const briefUrl = `${window.location.origin}/briefs/${id}`;
    await notifyOwners((ownerName) => supplierDeclinedEmail({
      ownerName,
      supplierName,
      briefName: brief?.brief_name,
      projectNumber: brief?.fusion_project_number,
      client: brief?.client,
      projectName: brief?.fusion_project_name,
      reasonLabel: null,
      reasonNote: note,
      briefUrl,
    }));
    navigate('/portal');
  };

  const EXTENSION_INFO = {
    heading: 'Request more time',
    body: "Lets Fusion 2K know you'd like longer to respond. They'll review and, if they can, extend your deadline – you'll see the new date here. You don't pick a date; just ask, and add a note if it helps.",
  };
  // Request a deadline extension. The supplier doesn't propose a new date –
  // they just signal they'd like more time (optional note for context). We
  // flag the recipient row and notify the brief owners; granting the actual
  // extension stays a Fusion 2K decision (the Extend Deadline tool on the
  // brief detail page, which clears this flag). Mirrors the decline notify
  // pattern but doesn't change status or navigate away.
  const handleRequestExtension = async () => {
    if (!recipient || extensionSubmitting) return;
    setExtensionSubmitting(true);
    setError(null);
    const note = (extensionNote || '').trim();
    const requestedAt = new Date().toISOString();
    const { error: updErr } = await supabase.from('brief_recipients').update({
      extension_requested_at: requestedAt,
      extension_request_note: note || null,
    }).eq('id', recipient.id);
    if (updErr) {
      setError(updErr.message);
      setExtensionSubmitting(false);
      return;
    }
    setRecipient(prev => prev ? { ...prev, extension_requested_at: requestedAt, extension_request_note: note || null } : prev);
    // Notify owners (respects the per-brief mute). The request is recorded
    // above regardless of whether the email goes out.
    const supplierName = await supplierNameForEmail();
    const briefUrl = `${window.location.origin}/briefs/${id}`;
    await notifyOwners((ownerName) => extensionRequestedEmail({
      ownerName,
      supplierName,
      briefName: brief?.brief_name,
      projectNumber: brief?.fusion_project_number,
      client: brief?.client,
      projectName: brief?.fusion_project_name,
      currentDeadline: deadlineIso ? fmtDateTime(deadlineIso) : null,
      note: note || null,
      briefUrl,
    }));
    setExtensionSubmitting(false);
    setShowExtensionModal(false);
  };

  // Reverse a previous decline. The supplier flipped a brief to 'declined'
  // earlier but the response deadline hasn't passed and the brief is still
  // sent – they should be able to change their mind and step back into the
  // Accept/Decline gate. We clear declined_at + decline_reason +
  // decline_reason_category so the row reads as a clean pending row again;
  // the activity timeline on the admin side loses the "X declined" event,
  // which is correct (the supplier undid the decline).
  //
  // No email fires here. The admin sees the status counter shift from
  // "1 declined" to "1 pending" on the brief detail page – visible enough
  // as a signal. The supplier still has to accept or re-decline before any
  // actionable email fires.
  const [reconsidering, setReconsidering] = useState(false);
  const handleReconsider = async () => {
    if (!recipient || reconsidering) return;
    if (!window.confirm('Reconsider this brief? It will move back to your Awaiting your response tab, where you can accept and submit before the deadline.')) return;
    setReconsidering(true);
    setError(null);
    const { error: updErr } = await supabase.from('brief_recipients').update({
      status: 'pending',
      declined_at: null,
      decline_reason: null,
      decline_reason_category: null,
    }).eq('id', recipient.id);
    if (updErr) {
      setError(updErr.message);
      setReconsidering(false);
      return;
    }
    setRecipient(prev => prev ? {
      ...prev,
      status: 'pending',
      declined_at: null,
      decline_reason: null,
      decline_reason_category: null,
    } : prev);
    setReconsidering(false);
  };

  // Multi-file attachment upload. Each file goes straight to the
  // brief-documents bucket on selection so the supplier sees confirmation
  // before submitting. The DB rows are only written to
  // brief_response_attachments when the response is submitted.
  // Per-file validation: any file that fails (size/extension/upload error)
  // is reported in the error list but doesn't block the others.
  const handleFilesSelected = async (fileList) => {
    if (!brief || !supplierUser || !fileList || fileList.length === 0) return;
    setUploadingDoc(true);
    setError(null);
    const files = Array.from(fileList);
    const newAttachments = [];
    const errors = [];
    for (const file of files) {
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_ATTACHMENT_EXTS.includes(ext)) {
        errors.push(`${file.name}: unsupported file type`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        errors.push(`${file.name}: too large (${(file.size / 1024 / 1024).toFixed(1)} MB, max 10 MB)`);
        continue;
      }
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${brief.id}/responses/${supplierUser.supplier_id}/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from('brief-documents')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (uploadError) {
        errors.push(`${file.name}: upload failed (${uploadError.message})`);
        continue;
      }
      const { data: pub } = supabase.storage.from('brief-documents').getPublicUrl(path);
      newAttachments.push({
        file_url: pub?.publicUrl || '',
        file_name: file.name,
        file_size_bytes: file.size,
        mime_type: file.type || null,
        uploaded_at: new Date().toISOString(),
      });
    }
    if (newAttachments.length > 0) {
      setAttachments(prev => [...prev, ...newAttachments]);
    }
    if (errors.length > 0) {
      setError(errors.join(' · '));
      setTimeout(() => setError(null), 10000);
    }
    setUploadingDoc(false);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!uploadingDoc) setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploadingDoc) return;
    handleFilesSelected(e.dataTransfer.files);
  };

  // Validates without committing – used to gate the Review modal so we don't
  // open it with an empty quote and force the supplier through an extra click.
  const validateForSubmit = () => {
    // BRIEF-26 – hard guard on the submit path: never accept a response once
    // the deadline has passed, even if the disabled UI is bypassed. (A DB-level
    // deadline check is the definitive server-side guard – flagged as a
    // follow-up migration; this blocks the client path.)
    if (deadlinePassed) { setError('The response deadline has passed – no response can be submitted for this brief.'); return false; }
    const filledLines = lineItems.filter(li =>
      (li.description && li.description.trim() !== '') ||
      Number(li.qty) > 0 ||
      Number(li.unit_price) > 0
    );
    if (isItemised) {
      if (filledLines.length === 0) { setError('Add at least one line item or switch to Single Total'); return false; }
      if (netSubtotal <= 0) { setError('Line items must total more than £0'); return false; }
    } else if (!form.total_price || Number(form.total_price) <= 0) {
      setError('Quote is required');
      return false;
    }
    // "Confirmation required by date" (deadline_date) is optional, but if set it
    // must be in the future – you can't require confirmation by a past date –
    // and on or before the event, if there is one.
    if (form.deadline_date) {
      const confirmBy = new Date(form.deadline_date);
      if (!isNaN(confirmBy) && confirmBy.getTime() <= Date.now()) {
        setError('Confirmation required by date must be in the future.');
        return false;
      }
      const eventStart = brief?.event_live_date ? new Date(brief.event_live_date) : null;
      if (eventStart && !isNaN(eventStart) && confirmBy.getTime() > eventStart.getTime()) {
        setError('Confirmation required by date should be on or before the event date.');
        return false;
      }
    }
    setError(null);
    return true;
  };

  // Clear a stale quote-required error the moment the quote becomes valid – it
  // was firing on an empty submit and then persisting after the supplier filled
  // the field, so the error contradicted the £-value they could see (nothing
  // cleared it until the next submit). Only clears the quote-related messages.
  useEffect(() => {
    if (netSubtotal > 0) {
      setError(prev => (
        prev === 'Quote is required'
        || prev === 'Line items must total more than £0'
        || prev === 'Add at least one line item or switch to Single Total'
      ) ? null : prev);
    }
  }, [netSubtotal]);

  // BRIEF-26 – archive a past-deadline brief from the supplier's view (mirrors
  // the dashboard's supplier_brief_archives upsert), then return to the portal.
  const [archiving, setArchiving] = useState(false);
  const handleArchive = async () => {
    if (!supplierUser || archiving) return;
    if (!window.confirm("Archive this brief from your view? Archived briefs are hidden from your portal. This can't be undone.")) return;
    setArchiving(true);
    const { error: archErr } = await supabase.from('supplier_brief_archives')
      .upsert({ supplier_id: supplierUser.supplier_id, brief_id: id }, { onConflict: 'supplier_id,brief_id' });
    if (archErr) { setError(archErr.message); setArchiving(false); return; }
    navigate('/portal');
  };

  const handleReviewClick = () => {
    if (!validateForSubmit()) return;
    setShowReviewModal(true);
  };

  // Notify every active brief owner of a supplier action – UNLESS the brief
  // is muted. The mute flag is re-read fresh at send time so a supplier tab
  // opened before Fusion muted still respects it. Mute stops the email only:
  // the DB writes that record the action have already happened by the time
  // this runs, so nothing is lost. `buildEmail(ownerName)` returns
  // { subject, html }. Fire-and-forget: failures never block the save.
  const notifyOwners = async (buildEmail) => {
    try {
      const { data: briefRow } = await supabase
        .from('briefs')
        .select('supplier_notifications_muted')
        .eq('id', id)
        .single();
      if (briefRow?.supplier_notifications_muted) return;
      const { data: ownerRows } = await supabase
        .from('brief_owners')
        .select('internal_users!user_id(full_name, email, is_active)')
        .eq('brief_id', id);
      const seen = new Set();
      for (const row of ownerRows || []) {
        const u = row.internal_users;
        if (!u || !u.email || u.is_active === false || seen.has(u.email.toLowerCase())) continue;
        seen.add(u.email.toLowerCase());
        const { subject, html } = buildEmail(u.full_name);
        // Per-owner try/catch so one bad address doesn't break out
        // of the loop and skip the remaining owners.
        try { await sendEmail({ to: u.email, subject, html }); } catch (err) {
          console.warn('Owner notification failed', { to: u.email, error: err?.message });
        }
      }
    } catch (e) {
      console.warn('Owner notification failed', e);
    }
  };

  const supplierNameForEmail = async () => {
    const { data: supplierRow } = await supabase
      .from('suppliers')
      .select('supplier_name')
      .eq('id', supplierUser.supplier_id)
      .single();
    return supplierRow?.supplier_name;
  };

  // Response submitted / re-saved – fired by the first submission and by
  // edit-after-submit re-saves, so Fusion 2K is alerted each time it changes.
  const notifyBriefOwners = async ({ isUpdate = false } = {}) => {
    const supplierName = await supplierNameForEmail();
    const briefUrl = `${window.location.origin}/briefs/${id}`;
    await notifyOwners((ownerName) => supplierResponseEmail({
      ownerName,
      supplierName,
      briefName: brief?.brief_name,
      totalPrice: netSubtotal,
      briefUrl,
      isUpdate,
    }));
  };

  const handleSubmit = async () => {
    if (!validateForSubmit()) return;
    // Belt-and-braces server-side guard: if the brief has been closed
    // (awarded or closed-without-award) since the page loaded, refuse
    // to write. Stops a stale browser tab from overwriting a
    // post-award quote, regardless of what the UI is showing.
    if (brief?.status === 'closed') {
      setError('This brief has been closed by Fusion 2K – responses can no longer be edited. Refresh the page to see the latest status.');
      return;
    }
    setSaving(true);
    setError(null);
    setShowReviewModal(false);
    const responseId = await writeResponseData({ submitted: true });
    if (!responseId) {
      // writeResponseData already surfaces the specific reason via setError
      // (deadline passed via the server trigger, RLS, constraint violation,
      // etc.). Only fall back to the generic message when no specific
      // error survived – the previous unconditional override hid the
      // exact reason from the supplier.
      setError(prev => prev || 'Submission failed. Try again.');
      setSaving(false);
      return;
    }
    // Stamp responded_at alongside the status flip so the activity feed
    // can surface a "Supplier responded" event and admin-side pages
    // (ResponseDetail, ComparisonView) can detect the response event
    // even if the brief_responses row later goes missing.
    //
    // 2026-06 amend-flow: also bump last_seen_version + clear
    // amend_pending_since. First submit is the act of "I've reviewed
    // and responded" – that's when CHANGED highlights stop being
    // useful and when the portal banner should clear for this brief.
    const respondedAt = new Date().toISOString();
    await supabase
      .from('brief_recipients')
      .update({
        status: 'responded',
        responded_at: respondedAt,
        last_seen_version: brief?.current_version || 1,
        amend_pending_since: null,
      })
      .eq('id', recipient.id);
    await notifyBriefOwners();
    // Review-and-submit brief §4 – no second modal. Flip the page to its
    // read-only submitted state (which persists: it still reads "with Fusion
    // 2K, editable until X" when they return tomorrow) and confirm with a
    // toast. The submitted state carries the revision deadline + Edit action +
    // a read-only summary already, so a modal would just duplicate it.
    // Re-read rather than patching one flag onto the stale row: the summary
    // that appears next is rendered FROM this object, so it has to be what was
    // actually saved.
    const { data: justSubmitted } = await supabase
      .from('brief_responses').select('*')
      .eq('brief_id', id).eq('supplier_id', supplierUser.supplier_id).maybeSingle();
    if (justSubmitted) setExistingResponse(justSubmitted);
    else setExistingResponse(prev => (prev ? { ...prev, is_submitted: true } : prev));
    setRecipient(prev => prev ? { ...prev, status: 'responded', responded_at: respondedAt } : prev);
    setSaving(false);
    setSubmitSuccess(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Re-save of an already-submitted response (edit-until-deadline). Unlike
  // the first submission it stays on the page (back to the read-only
  // summary), keeps the recipient status as 'responded' (the brief never
  // leaves the Submitted tab), refreshes the Last saved timestamp, and
  // re-notifies Fusion. Re-reads the deadline fresh before writing as a
  // belt-and-braces check on top of the DB trigger.
  const handleResubmit = async () => {
    if (!validateForSubmit()) return;
    // 2026-06 edit limit: 5 resubmits per amend round. Reads the
    // current edit_count fresh from the DB (rather than from in-
    // memory state) so concurrent edits from another tab can't slip
    // past the cap. The admin amend hook resets edit_count to 0,
    // so each amend gives the supplier a fresh window.
    const RESPONSE_EDIT_CAP = 5;
    const { data: freshResp } = await supabase
      .from('brief_responses')
      .select('edit_count')
      .eq('id', responseIdRef.current)
      .single();
    const currentEditCount = freshResp?.edit_count || 0;
    if (currentEditCount >= RESPONSE_EDIT_CAP) {
      setError(`You've reached the ${RESPONSE_EDIT_CAP}-edit limit on this response. If you need to change it again, contact Fusion to request a new amend.`);
      return;
    }
    // Authoritative deadline re-check at save time.
    const { data: freshRec } = await supabase
      .from('brief_recipients')
      .select('response_deadline')
      .eq('id', recipient.id)
      .single();
    const { data: freshBrief } = await supabase
      .from('briefs')
      .select('required_response_time')
      .eq('id', id)
      .single();
    const dl = freshRec?.response_deadline || freshBrief?.required_response_time || null;
    if (dl && Date.now() > new Date(dl).getTime()) {
      setError('The deadline has passed – your last saved version stands.');
      return;
    }
    setSaving(true);
    setError(null);
    // Capture the pre-edit state as the baseline version first, so a legacy
    // response (submitted before versioning) still yields an old→new diff on
    // its very first edit. No-op once a trail exists.
    await ensureOriginalSnapshot(responseIdRef.current);
    const responseId = await writeResponseData({ submitted: true });
    if (!responseId) {
      // writeResponseData surfaces the DB trigger's deadline rejection as
      // the inline error; just stop here.
      setSaving(false);
      return;
    }
    // Resubmitting after an amendment clears the amended-pending flag
    // AND bumps edit_count. Bundled into one update so the post-resubmit
    // state is consistent.
    await supabase.from('brief_responses').update({
      amended_pending: false,
      amended_since: null,
      edit_count: currentEditCount + 1,
    }).eq('id', responseId);
    if (amendedPending) setAmendedPending(false);
    // 2026-06 amend-flow: bump last_seen_version + clear
    // amend_pending_since on the recipient row. Resubmit IS the
    // supplier acknowledging the amendment – once that lands,
    // CHANGED highlights should clear and the portal banner should
    // drop this brief from its count.
    // status/responded_at are set here as well as on first submit. Amending a
    // brief flips every actionable recipient back to 'pending'; if the
    // supplier then answered by EDITING their existing response, this path ran
    // instead of the first-submit path and never moved them off 'pending'. The
    // admin side showed their quote and "edited twice" next to a PENDING chip,
    // and they still counted as yet-to-respond.
    //
    // responded_at only fills if it's empty – the first response is when they
    // responded; an edit isn't a new response.
    await supabase
      .from('brief_recipients')
      .update({
        status: 'responded',
        responded_at: recipient.responded_at || new Date().toISOString(),
        last_seen_version: brief?.current_version || 1,
        amend_pending_since: null,
      })
      .eq('id', recipient.id);
    // This is the edit-after-submit path, so the owner gets "has updated their
    // response" rather than a second "has responded" – which reads as a first
    // response arriving twice.
    await notifyBriefOwners({ isUpdate: true });
    setLastSavedAt(new Date());
    lastSavedHashRef.current = computeStateHash(form, isItemised, vatRate, lineItems, attachments);
    // Re-read the saved row before dropping back to the summary. The summary
    // renders from `existingResponse` – the row as it was when the page
    // loaded – so without this an edited quote saved correctly but the
    // supplier was shown their OLD total, which reads as "my change didn't
    // save" on the one screen where that must never be in doubt.
    const { data: saved } = await supabase
      .from('brief_responses').select('*')
      .eq('brief_id', id).eq('supplier_id', supplierUser.supplier_id).maybeSingle();
    if (saved) setExistingResponse(saved);
    setEditingSubmitted(false);
    setSaving(false);
  };

  // Cancel an in-progress edit of a submitted response: re-fetch the
  // last-saved version and repopulate, discarding any in-memory edits
  // (including staged document add/removes – nothing was committed), then
  // drop back to the read-only summary. Mirrors the loader's response
  // population so the summary reflects exactly what's on disk.
  const handleCancelEdit = async () => {
    if (!supplierUser) { setEditingSubmitted(false); return; }
    setSaving(true);
    const { data: res } = await supabase
      .from('brief_responses').select('*')
      .eq('brief_id', id).eq('supplier_id', supplierUser.supplier_id).single();
    if (res) {
      setExistingResponse(res);
      responseIdRef.current = res.id;
      if (res.last_autosaved_at) setLastSavedAt(new Date(res.last_autosaved_at));
      setForm({
        deadline_date: res.deadline_date || '',
        payment_schedule: res.payment_schedule || '',
        salient_points: res.salient_points || '',
        total_price: res.total_price || '',
        comments: res.comments || '',
        deposit_pct: res.deposit_pct ?? '',
        deposit_trigger: res.deposit_trigger || '',
        balance_terms: res.balance_terms || '',
        penalty_pct_per_day: res.penalty_pct_per_day ?? '',
      });
      setIsItemised(!!res.is_itemised);
      if (res.vat_rate != null) setVatRate(String(res.vat_rate));
      let nextLines = [emptyLineItem(0)];
      if (res.is_itemised) {
        const { data: li } = await supabase
          .from('brief_response_line_items').select('*')
          .eq('response_id', res.id).order('sort_order', { ascending: true });
        if (li && li.length > 0) {
          nextLines = li.map((row, idx) => {
            const qty = row.qty != null ? String(row.qty) : '1';
            const unit = row.unit_price != null ? String(row.unit_price) : '';
            const qNum = Number(qty); const pNum = Number(unit);
            const lt = (Number.isFinite(qNum) && Number.isFinite(pNum) && unit !== '') ? (qNum * pNum).toFixed(2) : '';
            return { description: row.description || '', qty, unit_price: unit, line_total: lt, sort_order: row.sort_order ?? idx };
          });
        }
      }
      setLineItems(nextLines);
      const { data: atts } = await supabase
        .from('brief_response_attachments').select('*')
        .eq('response_id', res.id).order('uploaded_at', { ascending: true });
      if (atts && atts.length > 0) {
        setAttachments(atts.map(a => ({ id: a.id, file_url: a.file_url, file_name: a.file_name, file_size_bytes: a.file_size_bytes, mime_type: a.mime_type, uploaded_at: a.uploaded_at, persisted: true })));
      } else if (res.supporting_doc_url) {
        setAttachments([{ file_url: res.supporting_doc_url, file_name: res.supporting_doc_name || 'Attached document', file_size_bytes: null, mime_type: null, uploaded_at: res.supporting_doc_uploaded_at || null, legacy: true }]);
      } else {
        setAttachments([]);
      }
    }
    setError(null);
    setEditingSubmitted(false);
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">Loading...</p>
    </div>
  );

  const inputClass = "w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";
  const labelClass = "font-['Metropolis'] text-xs font-semibold text-[#51534A] block mb-1";

  const isSubmitted = existingResponse?.is_submitted || recipient?.status === 'responded';
  const isDeclined = recipient?.status === 'declined';
  const isPending = recipient?.status === 'pending';
  const isAccepted = recipient?.status === 'accepted';

  // Effective deadline = per-recipient override when set, else brief-level.
  // Drives the editable-vs-locked split for submitted responses and the
  // within-the-hour countdown. nowTs ticks each second (submitted only).
  const deadlineIso = recipient?.response_deadline || brief?.required_response_time || null;
  const deadlineMs = deadlineIso ? new Date(deadlineIso).getTime() : null;
  const deadlinePassed = deadlineMs != null && nowTs >= deadlineMs;
  const msToDeadline = deadlineMs != null ? deadlineMs - nowTs : null;
  const withinHour = msToDeadline != null && msToDeadline > 0 && msToDeadline <= 3600000;
  // A submitted response locks when EITHER the deadline has passed OR
  // the brief has been closed (awarded or closed-without-award) – a
  // supplier shouldn't be able to revise their quote after Fusion has
  // made the award call, even if the original deadline is still in
  // the future. Both lock reasons surface as distinct copy beneath.
  const briefClosedAfterSubmit = brief?.status === 'closed';
  const responseLocked = deadlinePassed || briefClosedAfterSubmit;
  // Submitted response is editable while neither the deadline has
  // passed nor the brief has been closed; the reused form shows when
  // actively editing.
  // BRIEF-26 – deadline-passed lock for UNSUBMITTED responses. A pending or
  // accepted supplier whose deadline has passed (and the brief isn't formally
  // closed/awarded) can no longer submit: the form renders read-only with only
  // "Request extension" + "Archive". A response submitted BEFORE the deadline
  // is unaffected – that's the separate submitted-state lock below.
  const deadlineLockedUnsubmitted = deadlinePassed && !isSubmitted && !isDeclined
    && brief?.status !== 'closed' && (isPending || isAccepted);
  // Closed-brief lockdown (Closed-brief outcomes brief §2): once the brief is
  // closed NOTHING is editable – no form for a mid-draft (accepted) supplier,
  // no editing a submitted response. The derived outcome drives the copy below.
  const briefIsClosed = brief?.status === 'closed';
  const closedOutcome = deriveClosedOutcome({
    brief, recipient, response: existingResponse, supplierId: supplierUser?.supplier_id,
  });
  const editingResponseForm = !briefIsClosed && (isAccepted || deadlineLockedUnsubmitted
    || (isSubmitted && editingSubmitted && !responseLocked));
  const formLocked = deadlineLockedUnsubmitted || briefIsClosed;
  const lastSavedDisplay = lastSavedAt ? formatDeadline(lastSavedAt.toISOString()) : null;

  // Deadline-extension request. Outstanding once the supplier asks (until
  // Fusion grants an extension, which clears the flag). Offered while the
  // brief is still actionable for them (yet to respond) and open.
  const extensionRequested = !!recipient?.extension_requested_at;
  // Answered — refused outright, or countered with a date that is usually not
  // the one they asked for. Either way "Fusion 2K will be in touch" is no
  // longer true, and leaving it up is how someone waits for a reply they have
  // already had. The email carries the detail; this is the status.
  const extensionAnswered = !!recipient?.extension_answered_at;
  const canRequestExtension = (isPending || isAccepted) && brief?.status !== 'closed' && !extensionRequested;

  // Sticky action bar – carries ACTIONS in both states, never status alone
  // (response-form §1). Pending: Accept/Decline. Post-accept (editing the
  // form): Close / Review & submit + the live autosave status. Only the
  // terminal states (declined / deadline passed) fall back to a status chip.
  // Hidden once submitted or the brief is closed.
  // Sticky-bar deadline from the shared escalation scale, so the countdown
  // switches to hours in red under 48h – visible while the supplier is working
  // (§4). Live via barNow (§5).
  const barUrgency = deadlineUrgency(deadlineIso, {
    status: recipient?.status,
    briefStatus: brief?.status,
    now: barNow,
  });
  // Post-accept editing: the bar becomes the form's action bar.
  const barEditing = isAccepted && !formLocked;
  const barStatus = isDeclined
    ? { label: 'Declined', cls: 'text-[#9c2b12] bg-[#C6503A]/[0.10] border-[#C6503A]/25' }
    : formLocked
      ? { label: 'Deadline passed', cls: 'text-[#9c2b12] bg-[#C6503A]/[0.10] border-[#C6503A]/25' }
      : null;
  // Groundwork §5: the bar hides during the decline / request-more-time
  // confirmation flows – it must never offer "Review & submit" on a screen
  // that's discarding the response.
  const showActionBar = !loading && !!recipient && !!brief && brief.status !== 'closed'
    && !isSubmitted && !showDeclineModal && !showExtensionModal
    && (isPending || barEditing || !!barStatus);

  return (
    // pb-28 when the action bar is up, and it belongs HERE rather than on the
    // response container. The bar is position:fixed, so it is out of flow and
    // covers whatever the page ends with — and the page ends with the footer,
    // which is deliberately lifted OUT of the 920px container to go full-bleed.
    // Padding inside that container therefore cannot clear it; only padding on
    // the scrolling root can. (A comment on the bar claimed "the body's pb-24
    // clears it". There was no pb-24 anywhere in this file, and it would not
    // have helped the footer if there had been.)
    //
    // 7rem against a bar that measures ~64-72px in both layouts — the desktop
    // two-line title/deadline and the mobile full-width buttons — leaving room
    // for the backdrop blur's edge and a phone's home indicator.
    <div className={`min-h-screen bg-canvas ${showActionBar ? 'pb-28' : ''}`}>

      {/* Header – identical to the dashboard + /portal/compliance so
          every supplier portal page shares one chrome. Back-to-briefs
          sits in the body just below the header rather than in the
          header itself, keeping the pill on the right for account
          actions like the rest of the portal. */}
      <div className="bg-surface-sidebar border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-4 sm:px-10 h-[76px] sm:h-[90px] flex items-center justify-between gap-4">
          <div className="flex items-center min-w-0">
            <button type="button" onClick={() => navigate('/portal')} aria-label="Fusion 2K Supply Pilot – home" title="Home" className="flex items-center gap-2.5 flex-shrink-0 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity">
              <FusionLogo icon alt="Fusion" className="h-[38px] w-auto object-contain" />
              <span className="font-['Metropolis'] text-[19px] font-extrabold tracking-[0.14em] text-white leading-none">FUSION</span>
            </button>
            <div className="hidden sm:block w-px h-9 bg-white/15 flex-shrink-0 mx-3.5" />
            <span className="hidden sm:inline-flex items-center whitespace-nowrap font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase text-sb-text">
              Supply Pilot <span className="text-sb-muted font-semibold">&nbsp;·&nbsp; Partner Portal</span>
            </span>
          </div>
          {supplierUser && (
            <SupplierAccountMenu
              supplierName={supplierUser.suppliers?.supplier_name}
              supplierEmail={supplierUser.email || supplierUser.suppliers?.main_contact_email}
              initials={(supplierUser.suppliers?.supplier_name || '?').trim().charAt(0).toUpperCase()}
              onOpenProfile={() => openSlideout('profile')}
              onOpenHelp={() => openSlideout('help')}
              onChangePassword={() => navigate('/change-password')}
              onReplayTour={() => navigate('/portal', { state: { replayTour: true } })}
              onSignOut={() => { supabase.auth.signOut(); navigate('/'); }}
            />
          )}
        </div>
      </div>

      <div className="max-w-[920px] mx-auto px-6 sm:px-8 pt-10 pb-28">

        {/* Back-to-briefs – body-level so it stays at the top of the
            page in every state. Lives here rather than in the header
            so the header can keep the signed-in pill on the right and
            match the rest of the supplier portal. */}
        <button
          type="button"
          onClick={() => navigate('/portal')}
          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-fusion-mid-grey hover:text-active-green hover:border-active-green/50 flex w-fit items-center gap-2 mb-6 border border-line rounded-full px-3.5 py-1.5 transition-colors"
        >
          <span aria-hidden="true">←</span> Back to Briefs
        </button>

        {/* BRIEF-29 – declined resolution card leads the page. Neutral: soft
            red accent only (no red headline/fill), decline date, reason (when
            captured), Archive + Reopen (pre-deadline). */}
        {isDeclined && (
          <div className="mb-8 bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6" style={{ borderLeft: '4px solid #C6503A' }}>
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(198,80,58,0.12)' }} aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C6503A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6" /></svg>
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#9c2b12]">Declined</div>
                <h2 className="font-['Metropolis'] text-[20px] font-extrabold text-ink leading-tight mt-1">You declined this brief</h2>
                <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-1.5 leading-relaxed">
                  {recipient?.declined_at && <>You declined this on <strong className="text-ink">{fmtDateTime(recipient.declined_at)}</strong>. </>}It&rsquo;s kept here for your records.
                </p>
                {recipient?.decline_reason && (
                  <div className="mt-4 rounded-lg bg-[#F7F9F0] border border-[#EAF0DD] px-4 py-3">
                    <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#51534A] mb-1">Your reason</div>
                    <p className="font-['Metropolis'] text-[13px] text-[#4C4E42] leading-relaxed whitespace-pre-wrap">{recipient.decline_reason}</p>
                  </div>
                )}
                <div className="mt-5 flex items-center gap-3 flex-wrap">
                  {!deadlinePassed && (
                    <button
                      type="button"
                      onClick={handleReconsider}
                      disabled={reconsidering}
                      className="bg-white border-[1.5px] border-[#B7D9BF] rounded-lg text-[#009159] font-['Metropolis'] text-[13px] font-bold px-5 py-2.5 hover:border-[#009159] transition-colors disabled:opacity-50"
                    >
                      {reconsidering ? 'Reopening…' : 'Reopen and respond'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={archiving}
                    className="bg-white border border-[#D4D9C6] rounded-lg text-[#51534A] font-['Metropolis'] text-[13px] font-semibold px-5 py-2.5 hover:border-[#B9C0A9] transition-colors disabled:opacity-50"
                  >
                    {archiving ? 'Archiving…' : 'Archive brief'}
                  </button>
                </div>
                {!deadlinePassed && (
                  <p className="font-['Metropolis'] text-[11px] text-[#7C7F6F] mt-2.5">You can reopen any time before the response deadline.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* BRIEF-37 – closed-brief outcome resolution card. Leads the page for a
            closed brief the supplier didn't win. Neutral TEAL accent – not an
            error (red), not a win (green). The awarded-to-me case shows the
            celebration banner instead; declined shows its own card above. */}
        {briefIsClosed && !isDeclined && brief.awarded_supplier_id !== supplierUser?.supplier_id && closedOutcome && (() => {
          // Copy is DERIVED per state (brief §3) – this fixes the old bug where a
          // non-responder to an awarded brief was told "thanks for responding",
          // adds the draft-unsent state, and drops the hollow "we'll keep sending
          // you briefs" line.
          const copy = CLOSED_OUTCOME_COPY[closedOutcome];
          if (!copy) return null;
          const sid = supplierUser?.supplier_id;
          // §4 – the reason box renders the SAME text that was written per-brief
          // in the award flow: per-supplier override → all-suppliers body. Omit
          // entirely when nothing is stored (e.g. pre-feature awards). Only this
          // supplier's own text is ever reachable – never the winner's data.
          const resolvedReason = copy.reasonBox
            ? ((brief.not_selected_overrides && sid && brief.not_selected_overrides[sid]) || brief.not_selected_body || null)
            : null;
          return (
            <div className="mb-8 bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6" style={{ borderLeft: '4px solid #2E8FA1' }}>
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(46,143,161,0.12)' }} aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9 12l2 2 4-4" /></svg>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#2C6B82]">Outcome · {copy.eyebrow}</div>
                  <h2 className="font-['Metropolis'] text-[20px] font-extrabold text-ink leading-tight mt-1">{copy.heading}</h2>
                  <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-1.5 leading-relaxed">
                    {brief.closed_at && <>Closed on <strong className="text-ink">{fmtDateTime(brief.closed_at)}</strong>. </>}{copy.body}
                  </p>
                  {resolvedReason && (
                    <div className="mt-4 rounded-xl border border-[#E6EADB] bg-[#F7F9F0] px-4 py-3">
                      <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#2C6B82] mb-1">From Fusion 2K</div>
                      <p className="font-['Metropolis'] text-[13px] text-ink leading-relaxed whitespace-pre-wrap">{resolvedReason}</p>
                    </div>
                  )}
                  {closedOutcome === CLOSED_OUTCOME.DRAFT_UNSENT && (
                    <p className="font-['Metropolis'] text-[12px] text-[#7C7F6F] italic mt-3">Your draft is shown below, flagged as never submitted.</p>
                  )}
                  <div className="mt-5 flex items-center gap-4 flex-wrap">
                    <button
                      type="button"
                      onClick={handleArchive}
                      disabled={archiving}
                      className="bg-white border border-[#D4D9C6] rounded-lg text-[#51534A] font-['Metropolis'] text-[13px] font-semibold px-5 py-2.5 hover:border-[#B9C0A9] transition-colors disabled:opacity-50"
                    >
                      {archiving ? 'Archiving…' : 'Archive now'}
                    </button>
                    {/* Auto-archive countdown (§6) – 60 days after closing, so it
                        isn't a surprise. Derived from closed_at; matches the
                        portal's auto-archive rule. */}
                    {brief.closed_at && (() => {
                      const archiveAt = new Date(new Date(brief.closed_at).getTime() + 60 * 86400000);
                      return (
                        <span className="font-['Metropolis'] text-[12px] text-[#7C7F6F]">
                          Archives automatically on {fmtDate(archiveAt.toISOString())}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {(() => {
          // Status pill – mirrors the supplier-portal landing card states
          // so the brief detail feels like a continuation of the card the
          // supplier clicked, not a different view of the same data.
          // brief_category and nda_required are deliberately NOT surfaced
          // here – admin-side concerns the supplier doesn't need.
          const pill = (() => {
            if (isDeclined) return { label: 'Declined', cls: 'text-[#9c2b12] bg-[#C6503A]/[0.08] border-[#C6503A]/25' };
            // Closed-state check moves AHEAD of the "submitted" branch
            // so a brief that's been awarded (or closed without award)
            // shows the outcome – not the pre-award "Response submitted"
            // status. The supplier already submitted; the news now is
            // who won.
            if (brief.status === 'closed') {
              if (brief.awarded_supplier_id === supplierUser?.supplier_id) {
                // §5 – the SAME award-green token + component the dashboard and
                // brief-detail pills use, so "Awarded to you" can't drift into
                // a different green here. Only the label is specialised.
                return { ...portalStatusMeta('awarded'), label: 'Awarded to you' };
              }
              // Name the closed reason on the pill (muted grey), from the derived
              // outcome so it matches the outcome card + never mislabels a
              // non-responder.
              const closedSub = closedOutcome === CLOSED_OUTCOME.NOT_SELECTED ? 'Awarded elsewhere'
                : closedOutcome === CLOSED_OUTCOME.NO_AWARD ? 'No award'
                : closedOutcome === CLOSED_OUTCOME.DRAFT_UNSENT ? 'Draft not sent'
                : 'No response';
              return { label: `Closed · ${closedSub}`, cls: 'text-[#51534A] bg-[#51534A]/15 border-[#DADCCD]/30' };
            }
            if (isSubmitted) return { label: 'Response submitted', cls: 'text-fusion-forest bg-[#92D400]/10 border-[#92D400]/30' };
            // Deadline lapsed while still un-submitted – never show the teal
            // Colours + labels come from the shared PortalStatusPill source
            // (§3) so this pill can't drift from the dashboard's again.
            if (deadlinePassed) return portalStatusMeta('deadline_passed');
            if (isAccepted) return portalStatusMeta('in_progress');
            if (isPending) return portalStatusMeta('awaiting');
            return null;
          })();

          return (
            <>
              {/* Amendment banner – material changes since this supplier last
                  saw the brief. Clears on next visit (last_seen is stamped on
                  open). Silent edits never surface here. */}
              {/* §3 – a supplier who has ALREADY priced this brief gets the
                  full changes view: one row per change, what it was and what it
                  is now, and which changes plausibly move their number. The
                  question they're actually answering is "does my quote still
                  stand?", so their saved quote sits in the footer beside the
                  two things they can do about it.
                  A supplier who hasn't responded yet has no quote to defend, so
                  they keep the lighter banner below – the full diff would be
                  answering a question they haven't asked. */}
              {amendDiff && amendedPending && (
                <BriefChangesPanel
                  diff={amendDiff}
                  note={amendNote}
                  amendedAt={amendedAt}
                  versionNumber={brief?.current_version}
                  savedQuote={existingResponse?.total_price}
                  onUpdateResponse={() => { setError(null); setEditingSubmitted(true); }}
                  onReadFullBrief={() => {
                    const el = document.getElementById('brief-body');
                    el?.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
                    el?.focus();
                  }}
                />
              )}
              {amendDiff && !amendedPending && (
                <div className="flex items-start gap-3 bg-[#2E8FA1]/[0.06] border border-[#2E8FA1]/40 rounded-xl px-5 py-4 mb-6">
                  <svg className="w-5 h-5 text-[#2C6B82] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  <div className="flex-1 min-w-0">
                    <p className="font-['Metropolis'] text-[12px] font-extrabold tracking-[0.14em] uppercase text-[#2C6B82] mb-1.5">This brief has been updated by Fusion 2K</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">
                      {amendedPending
                        ? <>Fusion 2K has amended this brief since you submitted. <strong className="text-ink">Please review the changes</strong> – your previous response stands until you resubmit.</>
                        : 'Please review the latest version of this brief before responding.'}
                    </p>
                    {amendNote && <p className="font-['Metropolis'] text-sm text-fusion-mid-grey italic mt-2">"{amendNote}"</p>}
                    <ul className="mt-2 space-y-0.5 font-['Metropolis'] text-xs text-[#51534A]">
                      {amendDiff.fields.map((f, i) => <li key={i}>· {f.field} updated</li>)}
                      {amendDiff.body.changed && <li>· The brief was updated</li>}
                      {amendDiff.documents.map((d, i) => <li key={`d${i}`}>· Document {d.tag === 'add' ? 'added' : 'removed'}: {d.name}</li>)}
                    </ul>
                  </div>
                </div>
              )}
              {pill && (
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase mb-4 ${pill.cls}`}>
                  <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-current" />
                  {pill.label}{amendedPending ? ' · Brief amended' : ''}
                </span>
              )}

              <div className="flex items-baseline gap-3 flex-wrap mb-3">
                <h1 className="font-['Metropolis'] text-2xl sm:text-3xl font-extrabold tracking-tight text-ink-primary leading-tight">
                  {/* Title on its own line, never in prose (§6); safe fallback so
                      a title-less brief never shows blank or test copy. */}
                  {brief.brief_name?.trim() || 'Untitled brief'}
                </h1>
                {/* Title-row type pill – inline with the title so the supplier
                    sees the brief's framing immediately. Hidden when unflagged. */}
                {brief.brief_type === 'exclusive' && (
                  <span
                    className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-extrabold tracking-[0.14em] uppercase rounded-full px-2.5 py-1"
                    style={{ background: 'rgba(0,145,89,0.12)', color: '#009159', border: '1px solid rgba(0,145,89,0.4)' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
                    </svg>
                    Exclusive Brief
                  </span>
                )}
                {brief.brief_type === 'competitive' && brief.status !== 'closed' && !deadlinePassed && (
                  <span
                    className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-extrabold tracking-[0.14em] uppercase rounded-full px-2.5 py-1"
                    style={{ background: 'rgba(46,143,161,0.12)', color: '#2E8FA1', border: '1px solid rgba(46,143,161,0.4)' }}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <circle cx="8" cy="8" r="3" />
                      <circle cx="16" cy="8" r="3" />
                      <circle cx="12" cy="15" r="3" />
                    </svg>
                    Competitive Brief
                  </span>
                )}
              </div>

              {/* §1 – AWARDED STATE leads with the win, not an editing
                  restriction. Persistent: the award card no longer collapses
                  to a one-line contact summary once acknowledged – the award
                  is the most important thing on this page on every visit.
                  Order per brief: pill + title (above) → award card (amount,
                  awarded date, contact + Message) → what happens next → facts.
                  The read-only state is demoted to a small lock note on the
                  response card far below (see the Submitted block). */}
              {brief.status === 'closed' && brief.awarded_supplier_id === supplierUser?.supplier_id && (
                <>
                  {/* Split the moment (§1): CELEBRATION on the first view by this
                      user, a compact RECEIPT on every visit after. */}
                  {!awardCelebrationSeen ? (
                    // Celebration – streamers behind, one "you've won", value, one
                    // action. A page state, not a modal (§8): no focus trap.
                    <div className="relative overflow-hidden mb-6 rounded-2xl p-6 sm:p-8 text-center"
                      style={{ background: '#E7F3D6', border: '1px solid #CDE3B4', boxShadow: '0 1px 2px rgba(45,48,35,0.04), 0 14px 34px -12px rgba(0,113,63,0.26)' }}>
                      <span className="relative inline-flex justify-center" aria-hidden="true"><StreamersMark variant="full" size={82} /></span>
                      <h2 className="font-['Metropolis'] text-[24px] sm:text-[30px] font-extrabold text-ink leading-tight mt-1">You&rsquo;ve won this one</h2>
                      {brief.awarded_amount != null && (
                        <p className="font-['Metropolis'] text-[28px] font-extrabold text-[#00713f] mt-2">
                          &pound;{Number(brief.awarded_amount).toLocaleString('en-GB')} <span className="font-['Metropolis'] text-[15px] font-bold align-middle">ex-VAT</span>
                        </p>
                      )}
                      {primaryOwner?.email && (
                        <a href={`mailto:${primaryOwner.email}`}
                          className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-[#009159]/40 text-[#00713f] font-['Metropolis'] text-[13px] font-bold px-4 py-2.5 hover:bg-[#009159]/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159]/50 focus-visible:ring-offset-1 transition-colors">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v12H5.5L4 18.5V5z" /></svg>
                          Message {String(primaryOwner.name || '').split(' ')[0]}
                        </a>
                      )}
                    </div>
                  ) : (
                    // Receipt – the record. "Awarded to you" said ONCE (§3), value
                    // largest, then the commercial facts (only the ones that are
                    // set – no "Not set", the grid just closes up). No brief ref.
                    <div className="mb-6 rounded-2xl p-6 sm:p-7"
                      style={{ background: '#E7F3D6', border: '1px solid #CDE3B4', boxShadow: '0 1px 2px rgba(45,48,35,0.04), 0 12px 30px -12px rgba(0,113,63,0.22)' }}>
                      <div className="flex items-start gap-4">
                        {/* The medal replaces the glossy hexagon tick – one of
                            the five signals the rollout retires. 48px is the
                            brief's size for a supplier's awarded brief view. */}
                        <AwardMedal size={48} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2.5 flex-wrap">
                            {/* Supplier-facing copy is news addressed to them,
                                not the internal record's "Awarded · {date}". */}
                            <h2 className="font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase" style={{ color: 'var(--seal)' }}>You&rsquo;ve been awarded this brief</h2>
                            {brief.closed_at && <span className="font-['Metropolis'] text-[12px] text-[#51534A]">{formatDeadline(brief.closed_at)}</span>}
                          </div>
                          {brief.awarded_amount != null && (
                            <p className="font-['Metropolis'] text-[30px] font-extrabold text-[#00713f] leading-tight mt-1">
                              &pound;{Number(brief.awarded_amount).toLocaleString('en-GB')} <span className="font-['Metropolis'] text-[14px] font-bold align-middle">ex-VAT</span>
                            </p>
                          )}
                          {(() => {
                            const facts = [
                              { label: 'Event', value: brief.fusion_project_name },
                              { label: 'Client', value: brief.client },
                              { label: 'Event date', value: brief.event_live_date ? fmtDate(brief.event_live_date) : null },
                              { label: 'Payment terms', value: existingResponse?.payment_schedule },
                            ].filter(f => f.value && String(f.value).trim());
                            if (facts.length === 0) return null;
                            return (
                              <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                {facts.map(f => (
                                  <div key={f.label}>
                                    <dt className="font-['Metropolis'] text-[10px] font-bold tracking-[0.14em] uppercase text-[#51534A]">{f.label}</dt>
                                    <dd className="font-['Metropolis'] text-[13.5px] font-semibold text-ink mt-0.5 leading-snug">{f.value}</dd>
                                  </div>
                                ))}
                              </dl>
                            );
                          })()}
                          {primaryOwner && (
                            <div className="mt-4 pt-4 border-t border-[#CDE3B4] flex flex-wrap items-center gap-x-3 gap-y-2.5">
                              <span className="font-['Metropolis'] text-[13px] text-[#3a4a2a]">
                                Your Fusion 2K contact: <strong className="text-ink font-bold">{primaryOwner.name}</strong>
                              </span>
                              {primaryOwner.email && (
                                <a href={`mailto:${primaryOwner.email}`}
                                  className="sm:ml-auto inline-flex items-center justify-center gap-2 rounded-lg bg-white border border-[#009159]/40 text-[#00713f] font-['Metropolis'] text-[12.5px] font-bold px-3.5 py-2 hover:bg-[#009159]/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#009159]/50 focus-visible:ring-offset-1 transition-colors">
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v12H5.5L4 18.5V5z" /></svg>
                                  Message {String(primaryOwner.name || '').split(' ')[0]}
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* What happens next (§4) – the real process. Structure is
                      fixed; the copy is pending product-owner sign-off (flagged
                      in the report), so keep it factual and non-committal on
                      exact timings. */}
                  <div className="mb-8 bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6">
                    <div className="font-['Metropolis'] text-[11px] font-bold tracking-[0.2em] uppercase text-[#009159] mb-3.5">What happens next</div>
                    <ol className="space-y-3.5">
                      {[
                        { h: 'Fusion 2K will be in touch', b: 'Usually within a couple of working days, to confirm schedule, access and the finer detail of the work.' },
                        { h: 'Paperwork and purchase order', b: 'Fusion 2K raises a purchase order against the agreed amount. Invoice against that PO on the terms you agreed.' },
                        { h: 'Delivery and install', b: 'Work to the confirmed dates. If anything changes at your end, tell your Fusion 2K contact as early as you can.' },
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <span className="w-6 h-6 rounded-full bg-[#009159]/[0.12] text-[#009159] font-['Metropolis'] text-[12px] font-extrabold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                          <span className="font-['Metropolis'] text-[13.5px] text-[#4C4E42] leading-relaxed">
                            <strong className="text-ink font-bold">{step.h}</strong>. {step.b}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </>
              )}

              {/* Submission-confirmation brief §2: NO FS/project-number
                  reference is shown to suppliers – those are internal to Fusion
                  2K. This supersedes the earlier brief-detail §5 that added one
                  here; the reference line was removed. Briefs are identified by
                  name (the h1 above), with project name as secondary context
                  in the existing subtitle where useful. */}

              {/* BRIEF-36 §2 – the deadline-passed notice leads (directly under
                  the title/status). Gated on deadlineLockedUnsubmitted (open
                  brief, past deadline, no submission) – NOT formLocked, which
                  now also covers closed/awarded briefs. Those show their outcome
                  / award card instead; a "deadline passed, request extension"
                  notice is meaningless once the brief is decided. */}
              {deadlineLockedUnsubmitted && (
                <div className="mt-2 mb-8 flex items-start gap-4 rounded-[14px] p-5" style={{ background: '#fbeee9', border: '1px solid #f1cabb', borderLeft: '4px solid #E0290A', boxShadow: '0 1px 2px rgba(45,48,35,0.04), 0 6px 16px -6px rgba(224,41,10,0.16), 0 18px 36px -16px rgba(45,48,35,0.12)' }}>
                  <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(224,41,10,0.12)' }} aria-hidden="true">
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="#E0290A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5" /><path d="M12 16h.01" /></svg>
                  </span>
                  <div className="min-w-0">
                    <p className="font-['Metropolis'] text-[15px] font-bold text-ink leading-snug">The response deadline has passed.</p>
                    <p className="font-['Metropolis'] text-[13px] text-ink-2 mt-1.5 leading-relaxed">
                      This brief closed on <strong className="text-ink">{formatDeadline(deadlineIso)}</strong>, so you can no longer submit a response. You can still read the brief below. If you'd like to be considered, you can request a deadline extension – or archive this brief.
                    </p>
                  </div>
                </div>
              )}

              {/* Type context strip – earns its space by explaining what the
                  pill means. Suppliers won't know the convention by sight, so
                  the strip lands the framing once and clearly. Sits between
                  the title and the facts panel. Hidden when unflagged so
                  legacy briefs look exactly as they did before. */}
              {brief.brief_type === 'exclusive' && !deadlinePassed && (
                <div
                  className="flex items-start gap-3 rounded-lg px-4 py-3 mb-6"
                  style={{ background: 'rgba(0,145,89,0.05)', border: '1px solid rgba(0,145,89,0.25)' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#009159" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z" />
                  </svg>
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">
                    <strong className="text-ink">You're the only supplier being asked for this brief.</strong> Fusion 2K has approached you exclusively – take your time, ask any questions, and respond when you're ready.
                  </p>
                </div>
              )}
              {brief.brief_type === 'competitive' && brief.status !== 'closed' && (
                <div
                  className="flex items-start gap-3 rounded-lg px-4 py-3 mb-6"
                  style={{ background: 'rgba(46,143,161,0.06)', border: '1px solid rgba(46,143,161,0.28)' }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E8FA1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                    <circle cx="8" cy="8" r="3" />
                    <circle cx="16" cy="8" r="3" />
                    <circle cx="12" cy="15" r="3" />
                  </svg>
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey leading-relaxed">
                    <strong className="text-ink">This brief has been sent to multiple suppliers.</strong> Fusion 2K will review responses and award the work to one. Please respond with your strongest offer.
                  </p>
                </div>
              )}
              {/* Project name is no longer an unconditional subtitle – it's a
                  toggleable fact in the panel below (show_project_name),
                  alongside every other brief field. */}

              {/* Post-deadline status / update request – reviewing (day 1) then
                  ask-for-an-update (3 working days), submitters only, gone once
                  an outcome is recorded. */}
              {brief && supplierUser && (
                <BriefPostDeadline
                  briefId={brief.id}
                  supplierId={supplierUser.supplier_id}
                  supplierUserId={supplierUser.id}
                  deadlineIso={deadlineIso}
                  hasOutcome={brief.status === 'closed' || brief.status === 'cancelled'}
                  isSubmitted={isSubmitted}
                  quoteAmount={existingResponse?.total_price}
                  decisionBy={brief.decision_by || null}
                  briefName={brief.brief_name}
                  supplierName={supplierUser.suppliers?.supplier_name || supplierUser.supplier_name}
                />
              )}

              {/* §5 – a clarification was published after this supplier
                  submitted, so their quote may now be priced against stale
                  scope. Amber, top of page, links to the questions section. */}
              {isSubmitted && newClarCount > 0 && !clarBannerDismissed && (
                <div className="mb-6">
                  <Alert
                    tone="amber"
                    title={`${newClarCount} new clarification${newClarCount === 1 ? '' : 's'} since you submitted`}
                    body="An answer shared with everyone quoting was published after you sent your response. It may change what should be quoted – please review it and let Fusion 2K know if your quote needs to change."
                    action={{ label: 'Read the clarifications', onClick: () => { setClarBannerDismissed(true); const el = document.getElementById('brief-questions'); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } }}
                  />
                </div>
              )}

              {/* Facts panel – shared with the AddBrief 'What the
                  supplier sees' live preview via the same component,
                  so the two views stay in lockstep. Visibility flags
                  and value-presence rules live inside the component. */}
              <div className="mb-8">
                <SupplierBriefFactsPanel brief={brief} previousBrief={previousBrief} showInfo raised deadlineOverride={recipient?.response_deadline || null} awarded={brief.status === 'closed' && brief.awarded_supplier_id === supplierUser?.supplier_id} />
              </div>

              {/* The Brief body – plain text rather than a wrapped card,
                  per the redesign. Quiet fallback when a brief has no
                  body so the section never reads as a render bug. */}
              {/* §3: heading in the portal's forest-green tracked style (was
                  muted grey-brown ink-secondary), and the prose constrained to
                  a ~68ch reading measure – this is the one screen where a
                  supplier reads several hundred words. The facts card above
                  keeps its full width. */}
              {/* id is the target of "Read the full brief" in the changes
                  panel – tabIndex so focus follows the scroll for keyboard and
                  screen-reader users, not just the viewport. */}
              <div className="mb-10" id="brief-body" tabIndex={-1}>
                <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.22em] uppercase text-[#009159] mb-3">The Brief</p>
                {brief.brief_content && looksLikeHtml(brief.brief_content) ? (
                  <div className="sp-brief-content text-fusion-mid-grey leading-[1.7] max-w-[68ch]" dangerouslySetInnerHTML={{ __html: brief.brief_content }} />
                ) : brief.brief_content ? (
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey whitespace-pre-wrap leading-[1.7] max-w-[68ch]">{brief.brief_content}</p>
                ) : (
                  <p className="font-['Metropolis'] text-sm text-[#51534A] italic">No additional detail provided.</p>
                )}
              </div>

              {/* Supporting documents – entire section omitted when the
                  brief has none. */}
              {briefAttachments.length > 0 && (
                <div className="mb-10">
                  <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.22em] uppercase text-[#009159] mb-3">
                    Supporting Documents ({briefAttachments.length})
                  </p>
                  <div className="space-y-2">
                    {briefAttachments.map(a => (
                      <div key={a.id} className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift hover:shadow-lift-hover hover:-translate-y-0.5 transition-[transform,box-shadow] duration-150 px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          {/* Suppliers see the admin-chosen display name
                              when one's been set, otherwise the original
                              filename. */}
                          <p className="font-['Metropolis'] text-sm font-bold text-ink truncate">{a.display_name || a.file_name || 'Document'}</p>
                          {a.file_size_bytes != null && (
                            <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">{formatFileSize(a.file_size_bytes)}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => setViewerDoc({ url: a.file_url, name: a.display_name || a.file_name || 'Document', fileName: a.file_name, mime: a.mime_type })}
                            className="bg-transparent border border-[#2E8FA1] rounded-full text-[#2E8FA1] font-['Metropolis'] text-[11px] font-bold tracking-[0.08em] px-4 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadFile(a.file_url, a.file_name || a.display_name || '')}
                            className="bg-transparent border border-[#2E8FA1] rounded-full text-[#2E8FA1] font-['Metropolis'] text-[11px] font-bold tracking-[0.08em] px-4 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {/* Questions & clarifications – sits between the brief body and the
            respond card so a published answer (effectively a brief amendment)
            is read before the accept/decline decision. Only once the supplier
            can actually see the brief (not on the token/error states). */}
        {brief && supplierUser && (
          <BriefQuestions
            briefId={brief.id}
            supplierId={supplierUser.supplier_id}
            supplierUserId={supplierUser.id}
            questionsClose={brief.questions_close || null}
            responseDeadline={deadlineIso}
            briefClosed={brief.status === 'closed' || brief.status === 'cancelled'}
            submittedAt={existingResponse?.submitted_at || null}
            onNewCount={setNewClarCount}
            briefName={brief.brief_name}
            supplierName={supplierUser.suppliers?.supplier_name || supplierUser.supplier_name}
          />
        )}

        {/* === PENDING: Accept / Decline choice === */}
        {/* Guard against the live defect where a submitted/declined response
            coexisted with recipient.status still 'pending' (status-update lag),
            showing an Accept button above "Response Submitted". Once ANY
            response exists – draft, submitted, or declined – this card is gone;
            the response/declined state carries the edit route. */}
        {isPending && !existingResponse && !isSubmitted && !isDeclined && !showDeclineModal && !formLocked && (
          <div className="bg-white border border-line rounded-xl p-8 text-center">
            <h3 className="font-['Metropolis'] text-xl font-semibold tracking-tight text-ink-primary mb-3">Would you like to respond to this brief?</h3>
            {/* Single guidance line – the previous two paragraphs said
                the same thing twice. Kept the italic "you can still
                decline" framing because it adds the reassurance the
                shorter line doesn't. */}
            <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-8 italic">Accepting opens the response form. You can still decline if the brief isn't right for you.</p>
            <div className="flex items-center justify-center gap-6">
              <span className="inline-flex items-center">
                <button
                  onClick={() => setShowDeclineModal(true)}
                  className="bg-white border border-[#DADCCD] rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-8 py-4 hover:border-[#DADCCD] transition-colors"
                >
                  Decline Brief
                </button>
                <InfoIcon heading={DECLINE_INFO.heading} ariaLabel="What does declining this brief mean?">{DECLINE_INFO.body}</InfoIcon>
              </span>
              <button
                onClick={handleAccept}
                disabled={saving}
                className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-10 py-4 hover:bg-[#6a9a00] transition-colors disabled:opacity-50"
              >
                {saving ? 'Accepting...' : 'Accept Brief'}
              </button>
            </div>
            {/* Soft secondary affordance – ask for more time without
                committing to accept/decline yet. */}
            <div className="mt-6">
              {extensionAnswered ? (
                <p className="font-['Metropolis'] text-[12px] text-[#7A4E10]">Fusion 2K has replied to your request – check your email. The deadline above is the one that counts.</p>
              ) : extensionRequested ? (
                <p className="font-['Metropolis'] text-[12px] text-[#2C6B82]">Extension requested – Fusion 2K will be in touch.</p>
              ) : canRequestExtension ? (
                <span className="inline-flex items-center">
                  <button
                    type="button"
                    onClick={() => { setExtensionNote(''); setError(null); setShowExtensionModal(true); }}
                    className="bg-transparent border-0 p-0 font-['Metropolis'] text-[12px] text-[#2E8FA1] hover:text-active-green underline-offset-2 hover:underline cursor-pointer"
                  >
                    Need more time? Request a deadline extension
                  </button>
                  <InfoIcon heading={EXTENSION_INFO.heading} ariaLabel="What does requesting more time mean?">{EXTENSION_INFO.body}</InfoIcon>
                </span>
              ) : null}
            </div>
          </div>
        )}

        {/* === Decline modal – available from both Pending (not yet
            accepted) and In Progress (accepted, response started). When
            declining from In Progress we warn that the started response
            will be discarded. === */}
        {(isPending || isAccepted) && showDeclineModal && (
          <div className="bg-white border border-line rounded-xl p-8">
            <h3 className="font-['Metropolis'] text-xl font-semibold tracking-tight text-ink-primary mb-3">Decline Brief</h3>
            {isAccepted && (
              <p className="font-['Metropolis'] text-sm text-[#2C6B82] mb-3">
                You've already accepted this brief. Declining now will discard any response you've started and can't be undone.
              </p>
            )}
            <p className="font-['Metropolis'] text-sm text-[#51534A] mb-6">Required – a sentence or two (at least {DECLINE_REASON_MIN_CHARS} characters) so Fusion 2K can plan future briefs.</p>
            <div className="mb-6">
              <label className={labelClass}>Reason *</label>
              <AutoSizeTextarea
                minRows={4}
                className={inputClass}
                value={declineReason}
                onChange={e => { setDeclineReason(e.target.value); setError(null); }}
                placeholder="Please provide feedback on why you have declined this brief"
              />
              {/* Live char-count cue – surfaces the minimum-length
                  threshold so the supplier sees what's needed before
                  they hit Confirm. Goes amber until they cross the
                  threshold, then green. */}
              {(() => {
                const len = (declineReason || '').trim().length;
                const ok = len >= DECLINE_REASON_MIN_CHARS;
                return (
                  <p className={`font-['Metropolis'] text-[11px] mt-1.5 ${ok ? 'text-fusion-forest' : 'text-[#2C6B82]'}`}>
                    {ok
                      ? `${len} characters – looks good.`
                      : `${len} / ${DECLINE_REASON_MIN_CHARS} characters minimum`}
                  </p>
                );
              })()}
            </div>
            {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mb-4">{error}</p>}
            <div className="flex items-center gap-4">
              <button
                onClick={() => { setShowDeclineModal(false); setError(null); }}
                className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:border-[#DADCCD] transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleDeclineConfirm}
                disabled={saving}
                className="bg-[#E0290A] text-ink rounded-xl font-['Metropolis'] text-sm font-semibold px-8 py-3 hover:bg-[#b82008] transition-colors disabled:opacity-50"
              >
                {saving ? 'Submitting...' : 'Confirm Decline'}
              </button>
            </div>
          </div>
        )}

        {/* === Deadline-extension request modal === */}
        {showExtensionModal && createPortal(
          <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white border border-line rounded-xl w-full max-w-lg">
              <div className="p-8">
                <h3 className="font-['Metropolis'] text-xl font-semibold tracking-tight text-ink-primary mb-3">Request more time</h3>
                <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-2">
                  We'll let the Fusion 2K team know you'd like longer to respond to <span className="text-ink font-semibold">{brief?.brief_name}</span>. They'll review and, if they can, extend your deadline.
                </p>
                {deadlineIso && (
                  <p className="font-['Metropolis'] text-[12px] text-[#51534A] mb-5">Current deadline: <span className="text-fusion-mid-grey">{fmtDateTime(deadlineIso)}</span></p>
                )}
                <div className="mb-6">
                  <label className={labelClass}>Anything we should know? <span className="text-[#51534A] normal-case tracking-normal font-normal">(optional)</span></label>
                  <AutoSizeTextarea
                    minRows={3}
                    className={inputClass}
                    value={extensionNote}
                    onChange={e => setExtensionNote(e.target.value)}
                    placeholder="E.g. waiting on a quote from a sub-supplier, or away until Monday."
                  />
                </div>
                {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mb-4">{error}</p>}
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => { setShowExtensionModal(false); setError(null); }}
                    disabled={extensionSubmitting}
                    className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:border-[#DADCCD] transition-colors disabled:opacity-50"
                  >
                    Not now
                  </button>
                  <button
                    onClick={handleRequestExtension}
                    disabled={extensionSubmitting}
                    className="bg-[#2E8FA1] text-[#06262c] rounded-xl font-['Metropolis'] text-sm font-semibold px-8 py-3 hover:brightness-110 transition-[filter] disabled:opacity-50"
                  >
                    {extensionSubmitting ? 'Sending...' : 'Send request'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* === ACCEPTED: Response form === */}
        {editingResponseForm && !showDeclineModal && (
          <>
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              {/* §4: shared tracked forest-green section-heading style, matching
                  the card headings ("Your Quote" / "Response Details") and the
                  rest of the portal – was a one-off large sentence-case title. */}
              <h3 className="font-['Metropolis'] text-[11px] font-bold tracking-[0.22em] uppercase text-[#009159]">
                {formLocked ? 'Your response' : editingSubmitted ? 'Editing your response' : 'Your response'}
              </h3>
              {formLocked && (
                <span className="inline-flex items-center gap-1.5 font-['Metropolis'] text-[10px] font-bold tracking-[0.12em] uppercase rounded-full px-2.5 py-1 text-[#7C7F6F] bg-[#51534A]/[0.08]">
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                  {briefIsClosed ? 'Locked – brief closed' : 'Locked – the deadline has passed'}
                </span>
              )}
            </div>

            {/* Within-the-hour deadline warning – only while editing a
                submitted response in the final hour. Hidden once locked. */}
            {!formLocked && editingSubmitted && withinHour && (
              <div className="flex items-start gap-3 bg-[#E0290A]/[0.06] border border-[#E0290A]/30 rounded-lg px-4 py-3 mb-6">
                <svg className="w-[18px] h-[18px] text-[#E0290A] flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <div>
                  <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-[#E0290A] mb-0.5">Deadline approaching</p>
                  <p className="font-['Metropolis'] text-xs text-fusion-mid-grey leading-snug">
                    This brief closes at <strong className="text-ink">{formatDeadline(deadlineIso)}</strong> – <span className="text-[#E0290A] font-bold">{formatTimeRemaining(msToDeadline)}</span>. Your changes must be saved before then, or they won't be submitted and your last saved version will stand.
                  </p>
                </div>
              </div>
            )}

            {/* BRIEF-26 – a disabled fieldset locks (and mutes) every input at
                once when the deadline has passed; a saved draft stays visible
                read-only, empty fields render muted. */}
            <fieldset disabled={formLocked} className={formLocked ? 'space-y-6 opacity-60' : 'space-y-6'} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
              {/* === Your quote (BRIEF-25 §3) – elevated card with a result-
                  panel total === */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6">
                <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
                  <h4 className="font-['Metropolis'] text-sm font-bold tracking-[2px] uppercase text-[#009159]">Your quote *</h4>
                  <span className="inline-flex items-center">
                    <div className="inline-flex bg-white border border-line rounded-xl p-0.5">
                      <button
                        type="button"
                        onClick={() => setIsItemised(false)}
                        className={`font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-3 py-1.5 rounded-lg transition-colors ${!isItemised ? 'bg-[#92D400] text-[#131313]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`}
                      >
                        Single Total
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsItemised(true)}
                        className={`font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-3 py-1.5 rounded-lg transition-colors ${isItemised ? 'bg-[#92D400] text-[#131313]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`}
                      >
                        Itemised
                      </button>
                    </div>
                  </span>
                </div>

                {!isItemised && (
                  <div>
                    <label className={labelClass}>Quote <span className="text-[#51534A] font-normal normal-case tracking-normal">(ex-VAT)</span><InfoIcon heading="Your quote (ex-VAT)" ariaLabel="What does quote ex-VAT mean?">Your price excluding VAT. VAT is added below to show the grand total.</InfoIcon></label>
                    <MoneyInput value={form.total_price} onChange={(v) => set('total_price', v)} min="0" placeholder="0.00" />
                  </div>
                )}

                {isItemised && (
                  <div>
                    <div className="hidden md:grid grid-cols-12 gap-2 mb-2 px-1">
                      <div className="col-span-5 font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A]">Description</div>
                      <div className="col-span-2 font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A]">Qty</div>
                      <div className="col-span-2 font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A]">Unit Price</div>
                      <div className="col-span-2 font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] text-right">Line Total</div>
                      <div className="col-span-1"></div>
                    </div>
                    <div className="space-y-2">
                      {lineItems.map((li, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                          <input
                            type="text"
                            className={`${inputClass} col-span-12 md:col-span-5`}
                            value={li.description}
                            onChange={e => updateLineItem(idx, 'description', e.target.value)}
                            placeholder="e.g. Stage setup, lighting rig"
                          />
                          <input
                            type="number"
                            className={`${inputClass} col-span-4 md:col-span-2`}
                            value={li.qty}
                            onChange={e => updateLineItem(idx, 'qty', e.target.value)}
                            placeholder="1"
                            min="0"
                            step="any"
                          />
                          <div className="col-span-8 md:col-span-2">
                            <MoneyInput value={li.unit_price} onChange={(v) => updateLineItem(idx, 'unit_price', v)} min="0" placeholder="0.00" />
                          </div>
                          <div className="col-span-9 md:col-span-2 text-right font-['Metropolis'] text-sm text-fusion-mid-grey pr-1">
                            {li.line_total ? formatGBP(li.line_total) : <span className="text-[#51534A]">–</span>}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeLineItem(idx)}
                            className="col-span-3 md:col-span-1 bg-white border border-line rounded-xl text-[#51534A] hover:text-[#E0290A] hover:border-[#E0290A]/40 font-['Metropolis'] text-sm py-2 transition-colors"
                            title="Remove line"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="mt-3 bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:border-active-green/40 hover:text-active-green transition-colors"
                    >
                      + Add Line
                    </button>
                  </div>
                )}

                {/* VAT rate + result panel (BRIEF-25 §1) */}
                <div className="mt-6 pt-5 border-t border-[#E6EADB] flex flex-col sm:flex-row sm:items-start gap-5">
                  <div className="sm:w-44 flex-shrink-0">
                    <label className={labelClass}>VAT rate (%)</label>
                    <input
                      type="number"
                      className={inputClass}
                      value={vatRate}
                      onChange={e => setVatRate(e.target.value)}
                      onBlur={e => setVatRate(e.target.value === '' ? '20' : String(clampVat(e.target.value)))}
                      min="0"
                      max="20"
                      step="0.01"
                    />
                  </div>
                  <ResultPanel subtotal={netSubtotal} vatRate={vatRate} className="flex-1 w-full" />
                </div>
              </div>

              {/* === Additional support documents (BRIEF-25 §3) – elevated card. Moved above Response details 2026-08-18: it belongs with the quote it evidences === */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6">
                {/* Pass-2 §6: tracked forest-green section heading, matching
                    "Your quote" / "Response details" – was a sentence-case label. */}
                {/* Sits directly under Your quote, because that is what it is
                    for: the file that shows how the number was reached, added
                    while the number is still in mind rather than after the
                    rest of the form.

                    The formats are NOT repeated here — the dropzone states them
                    a few lines below, and saying them twice in one card left no
                    room to say what the upload is actually for. */}
                <p className="font-['Metropolis'] text-[10px] font-bold tracking-[0.22em] uppercase text-[#009159] mb-3">Additional support documents <span className="text-[#51534A] font-normal normal-case tracking-normal">(optional – support your costs with your own estimate attached here)</span></p>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`bg-white border-2 border-dashed rounded-xl p-6 transition-colors ${dragging ? 'border-[#92D400] bg-[#92D400]/5' : 'border-line'}`}
                >
                  <div className="flex flex-col items-center justify-center text-center gap-2">
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                      {dragging ? 'Drop to attach' : 'Drag and drop files here, or'}
                    </p>
                    {/* §4: secondary, not lime – one primary per screen, and
                        the screen's primary is Review & submit. */}
                    <label className="bg-white border border-[#DADCCD] text-fusion-forest rounded-xl font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:border-[#2E8FA1] hover:text-[#2E8FA1] transition-colors cursor-pointer whitespace-nowrap">
                      {uploadingDoc ? 'Uploading…' : '+ Choose files'}
                      <input
                        type="file"
                        multiple
                        className="hidden"
                        disabled={uploadingDoc}
                        accept={ALLOWED_ATTACHMENT_ACCEPT}
                        onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }} />
                    </label>
                    <p className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A]">PDF · Word · Excel · Images · max 10 MB each</p>
                  </div>
                </div>

                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((att, idx) => (
                      <div key={idx} className="bg-white border border-line rounded-xl px-4 py-3 flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <p className="font-['Metropolis'] text-sm text-ink truncate">{att.file_name}</p>
                          <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">
                            {formatFileSize(att.file_size_bytes) || (att.legacy ? 'Previously attached' : 'Attached')}
                            {!att.persisted && !att.legacy && ' · submit your response to send it through'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <a
                            href={att.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white rounded-xl border border-[#2E8FA1] text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors">
                            View
                          </a>
                          <button
                            type="button"
                            onClick={() => removeAttachment(idx)}
                            disabled={uploadingDoc}
                            className="bg-white rounded-xl border border-[#E0290A] text-[#E0290A] font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* === Response details (BRIEF-25 §3) – elevated card === */}
              <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 space-y-5">
                <h4 className="font-['Metropolis'] text-sm font-bold tracking-[2px] uppercase text-ink-secondary">Response details</h4>

              <div>
                <label className={labelClass}>Confirmation required by date<InfoIcon heading="Confirmation required by" ariaLabel="What is the confirmation required by date?">The date you need Fusion 2K to confirm the work for you by – for example, if you're holding the date or resources for them.</InfoIcon></label>
                <DatePicker
                  selected={toDate(form.deadline_date)}
                  onChange={(d) => set('deadline_date', fromDate(d))}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/yyyy --:--"
                  minDate={new Date()}
                  maxDate={brief?.event_live_date ? new Date(brief.event_live_date) : undefined}
                  isClearable
                  className={inputClass}
                  wrapperClassName="block w-full" />
              </div>
              <div>
                <label className={labelClass}>Payment terms</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="font-['Metropolis'] text-[11px] font-medium text-[#51534A] block mb-1">Deposit (%)</label>
                    <input type="number" min="0" max="100" step="1" inputMode="decimal" className={inputClass}
                      value={form.deposit_pct} onChange={e => set('deposit_pct', e.target.value)} placeholder="e.g. 50" />
                  </div>
                  <div>
                    <label className="font-['Metropolis'] text-[11px] font-medium text-[#51534A] block mb-1">Deposit due</label>
                    <select className={inputClass} value={form.deposit_trigger} onChange={e => set('deposit_trigger', e.target.value)}>
                      <option value="">–</option>
                      <option value="on_booking">On booking</option>
                      <option value="on_order">On order</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-['Metropolis'] text-[11px] font-medium text-[#51534A] block mb-1">Balance terms</label>
                    <input type="text" className={inputClass} value={form.balance_terms}
                      onChange={e => set('balance_terms', e.target.value)} placeholder="e.g. 30 days before the event" />
                  </div>
                  {/* Late-payment penalty removed (Rob, 2026-08-13). Asking a
                      supplier to state a daily interest rate on a quote form
                      invites a number nobody acts on, and it isn't Fusion's to
                      collect here. The COLUMN is left alone so anything already
                      entered survives; it just isn't asked for or shown. */}
                </div>
              </div>
              <div>
                <label className={labelClass}>Anything else about payment <span className="font-normal text-[#51534A]">(optional)</span></label>
                <AutoSizeTextarea minRows={2} className={inputClass} value={form.payment_schedule} onChange={e => set('payment_schedule', e.target.value)} placeholder="Anything that doesn't fit the fields above" />
              </div>
              <div>
                <label className={labelClass}>Salient Points<InfoIcon heading="Salient points" ariaLabel="What are salient points?">Anything you've specifically costed for that competitors might not have – what makes your quote stand out.</InfoIcon></label>
                <AutoSizeTextarea minRows={4} className={inputClass} value={form.salient_points} onChange={e => set('salient_points', e.target.value)} placeholder="Anything you have specifically costed for that others may not have included..." />
              </div>
              <div>
                <label className={labelClass}>Comments, restrictions and caveats</label>
                <AutoSizeTextarea minRows={4} className={inputClass} value={form.comments} onChange={e => set('comments', e.target.value)} placeholder="Any conditions, restrictions or additional notes..." />
              </div>
              </div>

            </fieldset>

            {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mt-4">{error}</p>}

            {formLocked ? (
              /* BRIEF-26 – locked action bar: no submit / save / decline. Only
                 Request extension (primary) + Archive (utility). */
              <div className="mt-6 bg-white border border-[#E6EADB] rounded-2xl shadow-lift px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
                <p className="font-['Metropolis'] text-xs text-[#51534A]">No response can be submitted for a closed brief.</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={handleArchive}
                    disabled={archiving}
                    className="bg-white border border-[#D4D9C6] rounded-lg text-[#51534A] font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:border-[#B9C0A9] transition-colors disabled:opacity-50"
                  >
                    {archiving ? 'Archiving…' : 'Archive brief'}
                  </button>
                  {extensionRequested ? (
                    <span className={`font-['Metropolis'] text-[12px] font-semibold inline-flex items-center px-2 ${extensionAnswered ? 'text-[#7A4E10]' : 'text-[#2C6B82]'}`}>{extensionAnswered ? 'Request answered' : 'Extension requested'}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setExtensionNote(''); setError(null); setShowExtensionModal(true); }}
                      className="bg-[#92D400] text-[#131313] rounded-lg font-['Metropolis'] text-sm font-bold px-6 py-3 shadow-[0_2px_8px_rgba(146,212,0,0.30)] hover:brightness-105 hover:-translate-y-px transition-[filter,transform]"
                    >
                      Request a deadline extension
                    </button>
                  )}
                </div>
              </div>
            ) : (
            <div className="mt-6 bg-white border border-[#E6EADB] rounded-2xl shadow-lift px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
              {/* §2: live autosave status, announced politely to AT. Says
                  what actually happened ("saved X ago") once a save has
                  occurred, and a reassuring "saves automatically" before the
                  first one – not a description of the 30s mechanism. */}
              <p aria-live="polite" className="font-['Metropolis'] text-xs text-[#51534A]">
                {editingSubmitted
                  ? (lastSavedDisplay ? `Last saved ${lastSavedDisplay}` : 'Edit and re-save before the deadline')
                  : (isAutoSaving
                    ? 'Saving…'
                    : (lastSavedAt
                      ? `Your draft saves automatically – ${formatSavedAgo(lastSavedAt).toLowerCase()}`
                      : 'Your draft saves automatically'))}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                {editingSubmitted ? (
                  /* Editing an already-submitted response – Cancel
                     discards in-memory edits; Save & Resubmit re-commits
                     and re-notifies Fusion. Stays in the Submitted tab. */
                  <>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="bg-transparent border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:border-[#DADCCD] transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <div className="flex flex-col items-start gap-1">
                      <button
                        onClick={handleResubmit}
                        disabled={saving || deadlinePassed || (existingResponse?.edit_count || 0) >= 5}
                        title={
                          deadlinePassed
                            ? 'The deadline has passed – your last saved version stands.'
                            : (existingResponse?.edit_count || 0) >= 5
                              ? 'You\'ve reached the 5-edit limit on this response. Contact Fusion to reset.'
                              : undefined
                        }
                        className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-8 py-3 hover:bg-[#6a9a00] transition-colors disabled:opacity-50"
                      >
                        {saving ? 'Saving…' : 'Save & Resubmit'}
                      </button>
                      {/* Edit-count cue (2026-06): shows how many edits
                          the supplier has remaining in this amend round.
                          Resets to 5 whenever Fusion amends the brief. */}
                      {existingResponse && (existingResponse.edit_count || 0) > 0 && (
                        <p className={`font-['Metropolis'] text-[11px] ${(existingResponse.edit_count || 0) >= 5 ? 'text-[#E0290A]' : 'text-[#51534A]'}`}>
                          {(existingResponse.edit_count || 0) >= 5
                            ? 'Edit limit reached. Contact Fusion to request a fresh round.'
                            : `${5 - (existingResponse.edit_count || 0)} of 5 edits left.`}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Decline is still available after accepting – a
                        supplier can accept then realise they can't take
                        it on. Opens the same decline modal (which warns
                        the started response will be discarded). */}
                    <span className="inline-flex items-center">
                      {/* Build #18 §6: Decline is consequential (hard to
                          reverse), so it carries a weightier outline (darker
                          #51534A border + bold ink) to stand apart from the
                          hairline utility buttons – NOT a red fill (red is
                          status, not action). */}
                      {/* §3: Decline is a normal business outcome, NOT
                          destructive – plain secondary, identical to its
                          pre-accept treatment. Red is reserved for genuinely
                          destructive actions. */}
                      <button
                        onClick={() => { setDeclineReasonCategory(''); setDeclineReason(''); setError(null); setShowDeclineModal(true); }}
                        disabled={saving}
                        className="bg-white border border-[#DADCCD] rounded-lg text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-3 hover:border-[#BFC7AE] transition-colors disabled:opacity-50"
                      >
                        Decline brief
                      </button>
                      <InfoIcon heading={DECLINE_INFO.heading} ariaLabel="What does declining this brief mean?">{DECLINE_INFO.body}</InfoIcon>
                    </span>
                    {/* Ask for more time – same row as Decline. Flips to a
                        static marker once requested (until Fusion grants it). */}
                    {extensionRequested ? (
                      <span className={`font-['Metropolis'] text-[12px] font-semibold inline-flex items-center px-2 ${extensionAnswered ? 'text-[#7A4E10]' : 'text-[#2C6B82]'}`}>{extensionAnswered ? 'Request answered' : 'Extension requested'}</span>
                    ) : canRequestExtension ? (
                      <span className="inline-flex items-center">
                        <button
                          onClick={() => { setExtensionNote(''); setError(null); setShowExtensionModal(true); }}
                          disabled={saving}
                          className="bg-transparent border border-[#DADCCD] rounded-xl text-[#51534A] font-['Metropolis'] text-sm font-semibold px-5 py-3 hover:border-[#2E8FA1] hover:text-[#2E8FA1] transition-colors disabled:opacity-50"
                        >
                          Request more time
                        </button>
                        <InfoIcon heading={EXTENSION_INFO.heading} ariaLabel="What does requesting more time mean?">{EXTENSION_INFO.body}</InfoIcon>
                      </span>
                    ) : null}
                    {/* Pass-2 §1: Close removed from here – it duplicated the
                        sticky-bar Close ~100px away, and the bar's is always
                        reachable. This block now holds only the two
                        "not proceeding" actions (Decline, Request more time),
                        which satisfies the §3 grouping without a separator.
                        Review & submit lives in the sticky bar. */}
                  </>
                )}
              </div>
            </div>
            )}
          </>
        )}

        {/* === ACCEPTED: Review & Submit modal === */}
        {/* Submission-confirmation brief §1 – success modal after the first
            submit. Sage scrim (not black), success mark + text (never colour
            alone), a receipt of the quote, a what-happens-next with the real
            revision window, and two clear actions. */}
        {/* Review-and-submit §4/§7 – a confirming TOAST, not a second modal.
            role="status" (announced, doesn't steal focus); auto-dismisses. The
            page beneath is already in its submitted state with the revision
            deadline + Edit action. */}
        {submitSuccess && createPortal(
          <div role="status" aria-live="polite"
            className="fixed right-6 bottom-6 z-[120] max-w-[92vw] bg-white border border-[#CDE3B4] rounded-xl shadow-lift px-4 py-3 flex items-start gap-3">
            <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,145,89,0.12)' }} aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#009159" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </span>
            <div className="min-w-0">
              <p className="font-['Metropolis'] text-[13px] font-bold text-ink">Response sent</p>
              <p className="font-['Metropolis'] text-[11px] text-[#4C4E42] mt-0.5">Fusion 2K has your quote{brief?.brief_name ? <> for <strong className="text-ink">{brief.brief_name}</strong></> : ''}.</p>
            </div>
            <button type="button" onClick={() => setSubmitSuccess(false)} aria-label="Dismiss" className="text-[#7C7F6F] hover:text-ink flex-shrink-0 -mr-1 -mt-1 p-1">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>,
          document.body
        )}

        {showReviewModal && createPortal(
          <div className="fixed inset-0 z-[100] bg-[#1b2415]/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            {/* §7 – dialog semantics + Escape returns to editing (see the effect
                above). Review owns "what you're about to send"; it deliberately
                does NOT explain what happens after (that was the duplication). */}
            <div role="dialog" aria-modal="true" aria-labelledby="review-heading" className="bg-white border border-line rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
              <div className="p-8 pb-4 border-b border-line">
                <h3 id="review-heading" className="font-['Metropolis'] text-xl font-semibold tracking-tight text-ink-primary">Check your response before sending</h3>
                {/* Brief title on its own line, never in a sentence (§2/§6). */}
                <p className="font-['Metropolis'] text-sm font-bold text-ink mt-1">{brief?.brief_name?.trim() || 'Untitled brief'}</p>
              </div>

              <div className="p-8 space-y-6 overflow-y-auto flex-1">
                {isItemised && (
                  <div>
                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-2">Line Items</p>
                    <div className="bg-white border border-line rounded-xl p-3 space-y-1.5">
                      {lineItems.filter(li => li.description || Number(li.qty) > 0 || Number(li.unit_price) > 0).map((li, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-center font-['Metropolis'] text-sm">
                          <div className="col-span-6 text-fusion-mid-grey truncate">{li.description || <span className="text-[#51534A] italic">No description</span>}</div>
                          <div className="col-span-3 text-[#51534A] text-right whitespace-nowrap">{li.qty || '0'} &times; {formatGBP(li.unit_price)}</div>
                          <div className="col-span-3 text-ink font-semibold text-right">{formatGBP(li.line_total)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <ResultPanel subtotal={netSubtotal} vatRate={vatRate} />

                {/* §2 – show the whole response, not just the money: quote type
                    + the confirmation date (the "delivery"/timing fact) +
                    payment terms below. */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Quote type</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{isItemised ? 'Itemised' : 'Single total'}</p>
                  </div>
                  {form.deadline_date && (
                    <div>
                      <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Confirmation required by</p>
                      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{fmtDateTime(form.deadline_date)}</p>
                    </div>
                  )}
                </div>

                {(form.deposit_pct !== '' || form.deposit_trigger || form.balance_terms || form.penalty_pct_per_day !== '') && (
                  <div>
                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Payment terms</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                      {[
                        form.deposit_pct !== '' && `${form.deposit_pct}% deposit${form.deposit_trigger ? ` ${DEPOSIT_TRIGGER_LABEL[form.deposit_trigger] || ''}` : ''}`,
                        form.balance_terms && `balance ${form.balance_terms}`,
                        form.penalty_pct_per_day !== '' && `${form.penalty_pct_per_day}% per day late-payment penalty`,
                      ].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                )}

                {form.payment_schedule && (
                  <div>
                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Anything else about payment</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey whitespace-pre-wrap">{form.payment_schedule}</p>
                  </div>
                )}

                {form.salient_points && (
                  <div>
                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Salient Points</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey whitespace-pre-wrap">{form.salient_points}</p>
                  </div>
                )}

                {form.comments && (
                  <div>
                    <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Comments, Restrictions and Caveats</p>
                    <p className="font-['Metropolis'] text-sm text-fusion-mid-grey whitespace-pre-wrap">{form.comments}</p>
                  </div>
                )}

                <div>
                  <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Attachments</p>
                  {attachments.length === 0 ? (
                    // §2 – a quote sent without its breakdown costs both sides a
                    // round trip. Warn (amber, announced), don't block.
                    <div role="status" className="flex items-start gap-2.5 rounded-lg bg-[#FBF3E4] border border-[#E89A2E]/40 px-3.5 py-2.5">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7A4E10" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0 mt-0.5"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                      <p className="font-['Metropolis'] text-[13px] text-[#5A3A0C] leading-snug">No attachments. If your quote has a breakdown or supporting document, adding it now saves a round trip – you can still send without one.</p>
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {attachments.map((a, idx) => (
                        <li key={idx} className="font-['Metropolis'] text-sm text-fusion-mid-grey truncate">• {a.file_name}{a.file_size_bytes != null ? ` · ${formatFileSize(a.file_size_bytes)}` : ''}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="p-8 pt-4 border-t border-line flex items-center justify-end gap-3 flex-wrap">
                <button
                  onClick={() => setShowReviewModal(false)}
                  disabled={saving}
                  className="bg-white border border-[#D4D9C6] rounded-lg text-[#51534A] font-['Metropolis'] text-sm font-semibold px-6 py-3 hover:border-[#B9C0A9] transition-colors disabled:opacity-50"
                >
                  Go back to edit
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="bg-[#92D400] text-[#131313] rounded-lg font-['Metropolis'] text-sm font-bold px-8 py-3 shadow-[0_2px_8px_rgba(146,212,0,0.30)] hover:brightness-105 hover:-translate-y-px transition-[filter,transform] disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {saving ? 'Sending…' : 'Send response'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* === RESPONDED: Submitted state – editable until the deadline,
            locked after. Hidden while actively editing (the reused form
            shows instead). State 1 (editable) vs State 3 (locked) split
            on deadlinePassed. === */}
        {isSubmitted && !editingSubmitted && (
          <div className={`bg-white border rounded-xl p-8 text-left ${deadlinePassed ? 'border-line opacity-95' : 'border-[#92D400]/30'}`}>
            {/* §5 – in the AWARDED state the award card above IS the headline;
                this section is now the quiet read-only record of what they
                submitted. "Accepted" is dropped (it clashed with the award
                pill and was ambiguous), and the editing restriction becomes a
                small lock note rather than a negative headline. */}
            <p className={`font-['Metropolis'] text-lg font-bold tracking-tight mb-2 ${deadlinePassed ? 'text-[#51534A]' : 'text-fusion-forest'}`}>{brief?.status === 'closed' && brief?.awarded_supplier_id === supplierUser?.supplier_id ? 'Your submitted response' : 'Response Submitted'}</p>
            <p className="font-['Metropolis'] text-sm text-[#51534A] max-w-xl">
              {/* §4 – the lock claim lives ONCE, in the footer below; this line
                  just says what the card is (no doubled "no longer editable"). */}
              {brief?.status === 'closed' && brief?.awarded_supplier_id === supplierUser?.supplier_id
                ? 'This is the quote Fusion 2K awarded.'
                : deadlinePassed
                ? 'The deadline has passed. Your response is final and can no longer be edited.'
                : 'Your response is with Fusion 2K. You can edit it until the deadline – just remember to save your changes.'}
            </p>
            {lastSavedDisplay && (
              <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.14em] uppercase text-[#51534A] mt-3">
                {/* Post-award (and once locked) "Last saved" is meaningless –
                    the record is fixed. Show "Submitted … <tz>" instead. */}
                {(brief?.status === 'closed' && brief?.awarded_supplier_id === supplierUser?.supplier_id) || deadlinePassed
                  ? <>Submitted {lastSavedDisplay}{localTimezoneAbbr() && localTimezoneAbbr() !== 'local' ? ` ${localTimezoneAbbr()}` : ''}</>
                  : <>Last saved {lastSavedDisplay}</>}
                {deadlinePassed && brief?.status !== 'closed' && deadlineIso && <> · Deadline {formatDeadline(deadlineIso)}</>}
              </p>
            )}

            {existingResponse?.is_itemised && lineItems.length > 0 && (
              <div className="mt-6 text-left bg-white border border-line rounded-xl p-4 max-w-2xl">
                <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-3">Line Items</p>
                <div className="space-y-1.5">
                  {lineItems.filter(li => li.description || Number(li.qty) > 0 || Number(li.unit_price) > 0).map((li, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center font-['Metropolis'] text-sm">
                      <div className="col-span-6 text-fusion-mid-grey truncate">{li.description || <span className="text-[#51534A] italic">No description</span>}</div>
                      <div className="col-span-3 text-[#51534A] text-right whitespace-nowrap">{li.qty || '0'} &times; {formatGBP(li.unit_price)}</div>
                      <div className="col-span-3 text-ink font-semibold text-right">{formatGBP(li.line_total)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(existingResponse?.total_price != null && existingResponse.total_price !== '') && (
              <ResultPanel subtotal={existingResponse.total_price} vatRate={existingResponse.vat_rate ?? 0} className="mt-6 max-w-sm" />
            )}

            {attachments.length > 0 && (
              <div className="mt-6">
                <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-3">
                  {attachments.length === 1 ? 'Attachment' : `${attachments.length} Attachments`}
                </p>
                <div className="flex flex-col items-start gap-2">
                  {attachments.map((att, idx) => (
                    <div key={idx} className="inline-flex items-center gap-3 bg-white border border-line rounded-xl px-4 py-2">
                      <div className="text-left min-w-0">
                        <p className="font-['Metropolis'] text-xs text-fusion-mid-grey truncate max-w-[280px]">{att.file_name}</p>
                        {att.file_size_bytes != null && (
                          <p className="font-['Metropolis'] text-[10px] text-[#51534A]">{formatFileSize(att.file_size_bytes)}</p>
                        )}
                      </div>
                      <a
                        href={att.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-xl border border-[#2E8FA1] text-[#2E8FA1] font-['Metropolis'] text-[10px] font-semibold tracking-[1px] uppercase px-3 py-1 hover:bg-[#2E8FA1]/10 transition-colors">
                        View
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer – editable (before deadline AND brief still open)
                or locked. Two distinct lock reasons get distinct copy
                so the supplier knows whether their window simply ran
                out or whether Fusion has moved on to award/close.
                Editing reopens the same response form pre-filled;
                nothing commits until Save & Resubmit. */}
            {!responseLocked ? (
              <div className="mt-7 pt-6 border-t border-line flex items-center justify-between gap-4 flex-wrap text-left">
                <p className="font-['Metropolis'] text-xs text-fusion-mid-grey leading-relaxed max-w-md">
                  You can still make changes – <strong className="text-ink">this brief closes {formatDeadline(deadlineIso)}{msToDeadline != null && <> ({formatTimeRemaining(msToDeadline)})</>}</strong>. Any edits must be saved before then.
                </p>
                <button
                  type="button"
                  onClick={() => { setError(null); setEditingSubmitted(true); }}
                  className="bg-transparent border border-[#92D400] rounded-xl text-fusion-forest font-['Metropolis'] text-sm font-bold px-6 py-2.5 hover:bg-active-green/[0.08] transition-colors whitespace-nowrap flex-shrink-0"
                >
                  Edit Response
                </button>
              </div>
            ) : (
              <div className="mt-7 pt-6 border-t border-line flex items-center gap-2.5 justify-start text-left">
                <svg className="w-[15px] h-[15px] text-[#51534A] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span className="font-['Metropolis'] text-xs text-[#51534A]">
                  {/* §4 – an awarded brief was AWARDED, not merely "closed";
                      say so accurately. This is the single lock message. */}
                  {brief?.status === 'closed' && brief?.awarded_supplier_id === supplierUser?.supplier_id
                    ? <>Locked when this brief was awarded. Contact Fusion 2K if something needs to change.</>
                    : briefClosedAfterSubmit
                    ? <>This response locked when Fusion 2K closed the brief. Contact them directly if something needs to change.</>
                    : <>This response locked when the deadline passed{deadlineIso && <> on {formatDeadline(deadlineIso)}</>}. Contact Fusion 2K directly if something needs to change.</>}
                </span>
              </div>
            )}
          </div>
        )}

        {/* === DECLINED: Read-only state === */}
        {isDeclined && (() => {
          // The supplier can step out of the declined state and back into
          // pending while the brief is still live AND the effective deadline
          // (per-recipient if extended, else the brief's) is still in the
          // future. Closed/awarded briefs offer no reconsider path – there's
          // nothing to reconsider into.
          const effectiveDeadline = recipient?.response_deadline || brief?.required_response_time;
          const canReconsider = brief?.status === 'sent'
            && effectiveDeadline
            && new Date(effectiveDeadline).getTime() > Date.now();
          return (
            <div className="bg-white border border-[#E0290A]/30 rounded-xl p-8">
              <p className="font-['Metropolis'] text-lg font-bold tracking-tight text-[#E0290A] mb-2">Brief Declined</p>
              <p className="font-['Metropolis'] text-sm text-[#51534A] mb-4">
                You declined this brief
                {recipient?.declined_at && <> on {fmtDate(recipient.declined_at)}</>}.
              </p>
              {recipient?.decline_reason && (
                <div className="bg-white border border-line rounded-xl px-4 py-3">
                  <p className="font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase text-[#51534A] mb-1">Your Reason</p>
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{recipient.decline_reason}</p>
                </div>
              )}
              {canReconsider && (
                <div className="mt-5 pt-5 border-t border-line">
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-3 leading-relaxed">
                    Changed your mind? You can still accept and submit this brief before the deadline on{' '}
                    <span className="text-ink font-semibold">
                      {fmtDateTime(effectiveDeadline)}
                    </span>.
                  </p>
                  <button
                    type="button"
                    onClick={handleReconsider}
                    disabled={reconsidering}
                    className="bg-white border border-[#92D400] rounded-xl text-fusion-forest font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-active-green/10 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="1 4 1 10 7 10" />
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    </svg>
                    {reconsidering ? 'Updating…' : 'Reconsider this brief'}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* End-of-page back link for read-only states. The header link
            scrolls off on long briefs, leaving the supplier with no
            obvious next step once they reach the Submitted / Declined
            block. This puts an exit right where their eye lands. */}
        {(isSubmitted || isDeclined) && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => navigate('/portal')}
              className="font-['Metropolis'] text-xs font-bold tracking-[2px] uppercase text-[#51534A] hover:text-active-green transition-colors inline-flex items-center gap-2 px-5 py-2"
            >
              <span aria-hidden="true">←</span> Back to Briefs
            </button>
          </div>
        )}

      </div>

      {/* Full-bleed band – lifted OUT of the 920px response container; its
          content takes that container's width so it still lines up. */}
      <SupplierFooter
        onBrowseHelp={() => openSlideout('help')}
        containerClassName="max-w-[920px] px-6 sm:px-8" />

      {/* Supporting-document viewer – shared component used by every
          supplier-facing surface so the experience stays consistent.
          Image viewer scales sensibly (small images no longer stranded
          in a huge black panel; large images get more room). PDFs +
          Office docs route through the iframe path inside the shared
          component. */}
      <AttachmentViewerModal doc={viewerDoc} onClose={() => setViewerDoc(null)} />

      <SupplierSlideout
        open={slideoutOpen}
        onClose={() => setSlideoutOpen(false)}
        initialTab={slideoutTab}
        supplier={supplierUser?.suppliers}
      />

      {/* Sticky action bar – accept/decline reachable without scrolling the
          whole brief. Fixed to the viewport bottom. Desktop shows the brief
          name + deadline on the left; mobile drops the text and the two
          buttons go full-width. The page root carries pb-28 while this is up
          so the footer clears it — see the note there. */}
      {showActionBar && (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-line bg-white/95 backdrop-blur-sm shadow-[0_-4px_16px_rgba(45,48,35,0.08)]">
          <div className="max-w-[920px] mx-auto px-6 sm:px-8 py-3 flex items-center gap-4">
            <div className="hidden sm:block min-w-0 flex-1">
              <div className="font-['Metropolis'] text-[13px] font-bold text-ink truncate">{brief.brief_name}</div>
              <div className="flex items-center gap-3 flex-wrap">
                {barUrgency && (
                  barUrgency.atRisk || barUrgency.band === 'overdue' ? (
                    <span aria-live="polite" className="font-['Metropolis'] text-[11px] font-bold text-[#E0290A] inline-flex items-center gap-1">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                      {barUrgency.label}
                    </span>
                  ) : (
                    <span className={`font-['Metropolis'] text-[11px] ${barUrgency.band === 'approaching' ? 'text-[#7A4E10] font-semibold' : 'text-fusion-mid-grey'}`}>{barUrgency.label}</span>
                  )
                )}
                {/* Live autosave status (§1) – announced politely to AT (§7). */}
                {barEditing && lastSavedDisplay && (
                  <span aria-live="polite" className="font-['Metropolis'] text-[11px] text-[#2E8FA1] inline-flex items-center gap-1">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
                    Draft saved {lastSavedDisplay}
                  </span>
                )}
              </div>
            </div>
            {isPending && !formLocked ? (
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setShowDeclineModal(true)}
                  className="flex-1 sm:flex-none bg-white border border-[#DADCCD] rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-6 py-2.5 hover:border-[#BFC7AE] transition-colors">
                  Decline
                </button>
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={saving}
                  className="flex-1 sm:flex-none bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-8 py-2.5 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                  {saving ? 'Accepting…' : 'Accept'}
                </button>
              </div>
            ) : barEditing ? (
              // Post-accept: the form's actions live here (§1) – Close + the
              // one primary, Review & submit, reachable from anywhere.
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={handleSaveAndClose}
                  disabled={saving}
                  className="flex-1 sm:flex-none bg-white border border-[#DADCCD] rounded-xl text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-6 py-2.5 hover:border-[#BFC7AE] transition-colors disabled:opacity-50">
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleReviewClick}
                  disabled={saving}
                  className="flex-1 sm:flex-none bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-8 py-2.5 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                  Review &amp; submit
                </button>
              </div>
            ) : barStatus ? (
              <span className={`ml-auto inline-flex items-center font-['Metropolis'] text-[11px] font-bold tracking-[0.12em] uppercase rounded-full border px-3.5 py-1.5 ${barStatus.cls}`}>{barStatus.label}</span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
